<!-- 新登录界面组件 - 支持用户名/邮箱/手机号登录、忘记密码、忘记用户名等功能 -->
<template>
  <div class="new-login-container">
    <!-- ==================== 页面头部区域 ==================== -->
    <LoginHeader :logo-path="LogoPath">
      <template v-slot:language>
        <language variant="white"></language>
      </template>
    </LoginHeader>

    <!-- ==================== 主内容区域 ==================== -->
    <!-- Main Content Area -->
    <div class="login-content">
      <!-- 登录成功加载动画 -->
      <div v-if="isLoginSuccess" class="login-success-loading">
        <div class="loading-spinner"></div>
        <div class="loading-text">{{ $t('lbl_emp2_Loading') }}</div>
      </div>
      
      <div class="new-login-card" :class="{ 'mobile-view': isMobile, 'login-success-blur': isLoginSuccess }">
        
        <div v-if="currentStep === 'username'" class="login-step login-step-username">
          <div class="step-title">{{ $t('lbl_emp2_SupplierLoginHere') }}</div>

          <form @submit.prevent="handleContinue" class="username-password-form">
            <div class="form-group username-group">
              <div class="input-wrapper">
                <div 
                  class="input-container-placeholder input-container-with-icon"
                  :class="{ 'input-error': usernameError }"
                >
                  <!-- 错误状态下的浮动标签 -->
                  <label v-show="usernameError || username || hasAutofillUsername" class="input-label" :class="{ 'label-error': usernameError }">{{ $t('lbl_emp2_AccountName') }}<span class="required">*</span></label>
                  <!-- 正常状态下的占位符 -->
                  <span v-if="!username && !usernameError && !hasAutofillUsername" class="input-placeholder">
                    {{ $t('lbl_emp2_AccountName') }} <span class="required">*</span>
                  </span>
                  <input
                    ref="usernameInput"
                    id="username-field"
                    v-model="username"
                    type="text"
                    name="username"
                    autocomplete="username"
                    class="form-input-placeholder"
                    :class="{ 'input-error': usernameError }"
                    placeholder=""
                    @focus="handleUsernameFocus"
                    @input="handleUsernameInput"
                    @change="handleUsernameInput"
                    @keydown.enter.prevent="handleUsernameEnter"
                  />
                  <div
                    class="input-icon-info"
                    tabindex="0"
                    @mouseenter="handleTooltipEnter"
                    @mouseleave="handleTooltipLeave"
                    @focus="handleTooltipEnter"
                    @blur="handleTooltipLeave"
                  >
                    <div class="input-icon-inner"></div>
                    <!-- Tooltip 内容 -->
                    <div v-if="showTooltip" class="login-custom-tooltip">
                      <div class="tooltip-wrapper">
                        <div class="login-tooltip-arrow"></div>
                        <div class="login-tooltip-content">
                          <div class="tooltip-text">
                            {{ $t('lbl_emp2_IfExhibitorCHINAPLAS') }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <!-- 错误信息 -->
                <div v-if="usernameError" class="username-error-message">
                  {{ $t('lbl_emp2_InvalidAccountName') }}
                </div>
              </div>
            </div>

            <!-- 密码输入框 -->
            <div class="form-group password-group">
              <div class="input-wrapper">
                <div class="password-input-wrapper">
                  <div class="input-container-placeholder" :class="{ 'input-error': passwordError }">
                    <!-- 浮动标签（有值或错误时显示在边框上方） -->
                    <label v-show="passwordError || password || hasAutofillPassword" class="input-label" :class="{ 'label-error': passwordError }">
                      {{ $t('lbl_emp2_Password') }} <span class="required">*</span>
                    </label>
                    <!-- 占位符（无值时显示在输入框内部） -->
                    <span v-if="!password && !passwordError && !hasAutofillPassword" class="input-placeholder">
                      {{ $t('lbl_emp2_Password') }} <span class="required">*</span>
                    </span>
                    <input
                      ref="passwordInput"
                      v-model="password"
                      :type="showPassword ? 'text' : 'password'"
                      name="password"
                      autocomplete="current-password"
                      class="form-input-placeholder"
                      :class="{ 'input-error': passwordError }"
                      placeholder=""
                      @focus="handlePasswordFocus"
                      @input="handlePasswordInput"
                      @change="handlePasswordInput"
                      @keydown.enter.prevent="handleContinue"
                    />
                  </div>
                  <button
                    type="button"
                    class="password-toggle"
                    @click="togglePassword"
                    t_label="btn_emp2_TogglePassword"
                  >
                    <svg v-if="!showPassword" width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" stroke="rgba(0,0,0,0.6)" stroke-width="2"/>
                      <line x1="1" y1="1" x2="23" y2="23" stroke="rgba(0,0,0,0.6)" stroke-width="2"/>
                    </svg>
                    <svg v-else width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="rgba(0,0,0,0.6)" stroke-width="2"/>
                      <circle cx="12" cy="12" r="3" stroke="rgba(0,0,0,0.6)" stroke-width="2"/>
                    </svg>
                  </button>
                </div>
                <!-- 错误信息 -->
                <div v-if="passwordError" class="password-error-message">
                  {{ errorMessage || $t('lbl_emp2_InvalidPassword') }}
                </div>
              </div>
            </div>

            <!-- 忘记链接 -->
            <div class="forgot-links-wrapper">
              <div class="forgot-text-container">
                <a href="#" @click.prevent="showForgotUsername" class="forgot-link-clickable">{{ $t('lbl_emp2_ForgotAccountIdOrPassword_prefix') }}{{ $t('lbl_emp2_ForgotAccountIdOrPassword_accountId') }}</a>
                <span class="forgot-text-static">{{ $t('lbl_emp2_ForgotAccountIdOrPassword_or') }}</span>
                <a href="#" @click.prevent="showForgotPassword" class="forgot-link-clickable">{{ $t('lbl_emp2_ForgotAccountIdOrPassword_password') }}</a>
                <span class="forgot-text-static">{{ $t('lbl_emp2_ForgotAccountIdOrPassword_suffix') }}</span>
              </div>
            </div>

            <button
              type="submit"
              class="btn-primary btn-continue-username username-continue-btn"
              :disabled="!username || !password "
              t_label="btn_emp2_Login"
            >
              {{ $t('lbl_emp2_Login') }}
            </button>
          </form>

          <Divider />

          <AlternativeLoginButtons
            :buttons="[
              { key: 'mobile', label: $t('lbl_emp2_LoginWithMobile'), withIndicator: true },
              { key: 'email', label: $t('lbl_emp2_LoginWithEmail'), withIndicator: true }
            ]"
            @switch="handleSwitchLogin"
          />

          <BecomeSupplierLink :url="supplierUrl" @click="openBecomeSupplierModal" />
        </div>

        <!-- ============================================ -->
        <!-- 【流程2】邮箱登录流程 - Step 1: 输入邮箱 -->
        <!-- ============================================ -->
        <div v-if="currentStep === 'email'" class="login-step login-step-email">
          <h1 class="step-title">{{ $t('lbl_emp2_SupplierLoginHere') }}</h1>
          
          <form @submit.prevent="handleEmailEnter" autocomplete="on">
            <div class="form-group">
              <div class="input-wrapper">
                <div class="input-container-email" :class="{ 'input-error': emailError }">
                  <!-- 浮动标签（有值或错误时显示在边框上方） -->
                  <label v-if="emailError || email" class="input-label email-label" :class="{ 'label-error': emailError }">
                    {{ $t('lbl_emp2_Email') }} <span class="required">*</span>
                  </label>
                  <!-- 占位符（无值时显示在输入框内部） -->
                  <span v-if="!email && !emailError" class="email-placeholder">
                    {{ $t('lbl_emp2_Email') }} <span class="required">*</span>
                  </span>
                  <input
                    v-model="email"
                    type="email"
                    name="email"
                    id="email-field"
                    autocomplete="email"
                    class="form-input-email"
                    :class="{ 'input-error': emailError }"
                    placeholder=""
                    @focus="clearEmailError"
                    @input="handleEmailInput"
                    @keydown.enter.prevent="handleEmailEnter"
                  />
                </div>
                <!-- 错误信息 -->
                <div v-if="emailError" class="email-error-message">
                  {{ $t('lbl_emp2_InvalidEmail') }}
                </div>
              </div>
            </div>

            <button
              type="submit"
              class="btn-primary"
              :disabled="!isValidEmail || loading"
              t_label="btn_emp2_Continue_Email"
            >
              {{ $t('lbl_emp2_Continue') }}
            </button>
          </form>

          <Divider />

          <AlternativeLoginButtons
            :buttons="[
              { key: 'username', label: $t('lbl_emp2_LoginWithUsername'), withIndicator: false },
              { key: 'mobile', label: $t('lbl_emp2_LoginWithMobile'), withIndicator: false }
            ]"
            @switch="handleSwitchLogin"
          />

          <BecomeSupplierLink :url="supplierUrl" @click="openBecomeSupplierModal" />
        </div>

        <!-- ============================================ -->
        <!-- 【流程3】手机号登录流程 - Step 1: 输入手机号 -->
        <!-- ============================================ -->
        <MobileStep
          v-if="currentStep === 'mobile'"
          :country-code="countryCode"
          :country-options="countryOptions"
          :mobile-number="mobileNumber"
          :mobile-error="mobileError"
          :loading="loading"
          @update:countryCode="handleCountryCodeUpdate($event)"
          @update:mobileNumber="mobileNumber = $event"
          @clear-mobile-error="clearMobileError"
          @continue="handleMobileContinue"
          @switch-login="handleSwitchLogin"
          @show-become-supplier="openBecomeSupplierModal"
        />

        <!-- ============================================ -->
        <!-- 【通用步骤】Step 2: 输入密码 -->
        <!-- 适用于：用户名登录、邮箱登录、手机号登录 -->
        <!-- ============================================ -->
        <div v-if="currentStep === 'password'" class="login-step">
          <div class="step-header">
            <button type="button" class="back-btn" @click="goBack" t_label="btn_emp2_Back_Password">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M12 4L6 10L12 16" stroke="rgba(0,0,0,0.8)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <line x1="6" y1="10" x2="16" y2="10" stroke="rgba(0,0,0,0.8)" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </button>
            <h1 class="step-title">{{ $t('lbl_emp3_EnterPassword') }}</h1>
          </div>

          <!-- 按照原型图：Logging into、Password输入框、Forgot Password? 在一个容器内，gap: 16px -->
          <form @submit.prevent="handlePasswordLogin" class="password-form-container">
            <!-- 用户名显示文本 -->
            <div class="password-username-display">
              <span>{{ $t('lbl_emp2_LoggingInto') }} </span>
              <strong>{{ loginIdentifier }}</strong>
            </div>
            
            <!-- 隐藏的用户名输入框，用于浏览器识别登录表单 -->
            <input
              v-if="loginMethod === 'username'"
              type="text"
              :value="username"
              name="username"
              autocomplete="username"
              style="position: absolute; left: -9999px; width: 1px; height: 1px; opacity: 0;"
              tabindex="-1"
            />
            <input
              v-else-if="loginMethod === 'email'"
              type="email"
              :value="email"
              name="email"
              autocomplete="username"
              style="position: absolute; left: -9999px; width: 1px; height: 1px; opacity: 0;"
              tabindex="-1"
            />
            <input
              v-else-if="loginMethod === 'mobile'"
              type="tel"
              :value="countryCode + mobileNumber"
              name="phone"
              autocomplete="username"
              style="position: absolute; left: -9999px; width: 1px; height: 1px; opacity: 0;"
              tabindex="-1"
            />
            
            <!-- 密码输入框 -->
            <div class="input-wrapper">
              <div class="password-input-wrapper">
                <div class="input-container-placeholder" :class="{ 'input-error': passwordError }">
                  <!-- 浮动标签（有值或错误时显示在边框上方） -->
                  <label v-show="passwordError || password" class="input-label" :class="{ 'label-error': passwordError }">
                    {{ $t('lbl_emp2_Password') }} <span class="required">*</span>
                  </label>
                  <!-- 占位符（无值时显示在输入框内部） -->
                  <span v-if="!password && !passwordError" class="input-placeholder">
                    {{ $t('lbl_emp2_Password') }} <span class="required">*</span>
                  </span>
                  <input
                    ref="passwordInput"
                    v-model="password"
                    :type="showPassword ? 'text' : 'password'"
                    name="password"
                    autocomplete="current-password"
                    class="form-input-placeholder"
                    :class="{ 'input-error': passwordError }"
                    placeholder=""
                    @focus="clearPasswordError"
                    @input="handlePasswordInput"
                    @change="handlePasswordInput"
                  />
                </div>
                <button
                  type="button"
                  class="password-toggle"
                  @click="togglePassword"
                  t_label="btn_emp2_TogglePassword"
                >
                  <svg v-if="!showPassword" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" stroke="rgba(0,0,0,0.6)" stroke-width="2"/>
                    <line x1="1" y1="1" x2="23" y2="23" stroke="rgba(0,0,0,0.6)" stroke-width="2"/>
                  </svg>
                  <svg v-else width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="rgba(0,0,0,0.6)" stroke-width="2"/>
                    <circle cx="12" cy="12" r="3" stroke="rgba(0,0,0,0.6)" stroke-width="2"/>
                  </svg>
                </button>
              </div>
              <!-- 错误信息 -->
              <div v-if="passwordError" class="password-error-message">
                {{ errorMessage || $t('lbl_emp2_InvalidPassword') }}
              </div>
            </div>
            
            <!-- Forgot Password? 链接 -->
            <div class="forgot-link-wrapper">
              <a href="#" @click.prevent="showForgotPassword" class="forgot-link">{{ $t('lbl_emp2_ForgotPasswordLink') }}</a>
            </div>

            <button
              type="submit"
              class="btn-primary"
              :disabled="!password || loading"
              t_label="btn_emp2_Login"
            >
              {{ $t('lbl_emp2_Login') }}
            </button>
          </form>

          <Divider />

          <!-- 流程2（email/mobile登录）：显示"Send code to log in"按钮 -->
          <AlternativeLoginButtons
            v-if="loginMethod === 'email' || loginMethod === 'mobile'"
            :buttons="[
              { key: 'otp', label: loginMethod === 'email' ? $t('lbl_emp2_LoginByVerificationCodeEmail', { emailInput: email }) : $t('lbl_emp2_LoginByVerificationCodeMobile', { countryCode: countryCodePlain, mobileNumber: mobileNumber }), withIndicator: false }
            ]"
            @switch="handleSwitchLogin"
          />

          <!-- 流程1（username登录）：显示"Login with Mobile"、"Login with Email" -->
          <div v-else class="alternative-login">
            <button
              class="btn-secondary btn-with-indicator"
              @click="switchToMobile"
              t_label="btn_emp2_LoginWithMobile_FromPassword"
            >
              {{ $t('lbl_emp2_LoginWithMobile') }}
            </button>
            <button
              class="btn-secondary btn-with-indicator"
              @click="switchToEmail"
              t_label="btn_emp2_LoginWithEmail_FromPassword"
            >
              {{ $t('lbl_emp2_LoginWithEmail') }}
            </button>
          </div>
        </div>

        <!-- Step 2: Enter OTP/Verification Code -->
        <div v-if="currentStep === 'otp'" class="login-step login-step-otp">
          <div class="step-header">
            <button type="button" class="back-btn" @click="goBack" t_label="btn_emp2_Back_OTP">
              <img 
                src="https://cdn.adsalecdn.com/EMP/2-0/BuyerIdx/All_Categories.svg" 
                alt="back" 
                class="back-icon"
              />
            </button>
            <h1 class="step-title">{{ $t('lbl_emp2_EnterVerificationCode') }}</h1>
          </div>

          <div class="form-group">
            <div class="otp-info">
              <template v-if="loginMethod === 'email'">
                <div class="otp-info-line">{{ $t('lbl_emp2_CodeSentToEmail', { emailInput: '' }).replace(': ', '') }}</div>
                <div class="otp-info-target">{{ email }}</div>
              </template>
              <template v-else>
                <div class="otp-info-line">{{ $t('lbl_emp2_CodeSentToMobile', { countryCode: '', mobileNumber: '' }).replace('. ', '') }}</div>
                <div class="otp-info-target">{{ countryCode }} {{ mobileNumber }}</div>
              </template>
            </div>
            <div class="otp-input-group">
              <label class="otp-label">{{ $t('lbl_emp2_PleaseEnterCodeToLogin') }}:</label>
              <div class="otp-inputs" :class="{ 'otp-error': otpError }">
                <input
                  v-for="(digit, index) in otpDigits"
                  :key="index"
                  :value="digit"
                  type="text"
                  inputmode="numeric"
                  pattern="[0-9]*"
                  class="otp-input"
                  :class="{ 'otp-input-error': otpError }"
                  maxlength="1"
                  @input="handleOtpInput(index, $event)"
                  @keydown="handleOtpKeydown(index, $event)"
                  @paste="handleOtpPaste($event)"
                  @focus="clearOtpError"
                />
              </div>
              <!-- 错误信息 -->
              <div v-if="otpError" class="otp-error-message">
                {{ $t('lbl_emp2_InvalidVerificationCode') }}
              </div>
              <!-- Loading 状态 -->
              <div v-if="loading && currentStep === 'otp'" class="otp-loading-message">
                {{ $t('lbl_emp2_Loading') }}
              </div>
            </div>
            <div class="otp-help-text">
              <template v-if="loginMethod === 'email'">
                <!-- Email：保留原来的 v-html（包含 no-reply / spam folder 加粗） -->
                <p v-html="$t('lbl_emp2_IfNotReceivedEmailVerification')"></p>
              </template>
              <template v-else>
                <!-- Mobile：直接使用翻译中的 HTML，加粗 spam / blocked messages -->
                <p v-html="$t('lbl_emp2_IfNotReceivedSMSVerification')"></p>
              </template>
            </div>
            <button
              class="btn-resend"
              :disabled="resendCountdown > 0"
              @click="resendOTP"
              t_label="btn_emp2_ResendOTP"
            >
              {{ resendCountdown > 0 ? $t('lbl_emp2_ResendAfterSec', { countDown: resendCountdown }) : $t('lbl_emp2_ResendCode') }}
            </button>
          </div>

          <Divider />

          <div class="alternative-login">
            <button class="btn-secondary" @click="handleSwitchLogin('password')" t_label="btn_emp2_LoginByPassword_FromOTP">
              <div class="btn-secondary-content">
                <template v-if="loginMethod === 'email'">
                  <span class="btn-secondary-text" v-html="$t('lbl_emp2_LoginByPasswordEmailHtml', { emailInput: email })"></span>
                </template>
                <template v-else>
                  <span class="btn-secondary-text" v-html="$t('lbl_emp2_LoginByPasswordMobileHtml', { countryCode: countryCodePlain, mobileNumber: mobileNumber })"></span>
                </template>
              </div>
            </button>
          </div>
        </div>

        <!-- ============================================ -->
        <!-- 【流程4】忘记密码流程 - 选项页面 -->
        <!-- ============================================ -->
        <ForgotPasswordOptions
          v-if="currentStep === 'forgot-password-options'"
          @go-back="goBack"
          @switch-login="handleSwitchLogin"
        />

        <!-- ============================================ -->
        <!-- 【流程4】忘记密码流程 - 方式1: 通过邮箱重置 -->
        <!-- ============================================ -->
        <ForgotPasswordEmail
          v-if="currentStep === 'forgot-password-email'"
          :forgot-email="forgotEmail"
          :forgot-email-error="forgotEmailError"
          :is-valid-forgot-email="isValidForgotEmail"
          :loading="loading"
          @update:forgotEmail="forgotEmail = $event"
          @clear-forgot-email-error="clearForgotEmailError"
          @go-back="goBack"
          @continue="handleForgotPasswordEmail"
        />

        <!-- ============================================ -->
        <!-- 【流程4】忘记密码流程 - 方式2: 通过手机号重置 -->
        <!-- ============================================ -->
        <ForgotPasswordMobile
          v-if="currentStep === 'forgot-password-mobile'"
          :country-code="forgotCountryCode"
          :country-options="countryOptions"
          :mobile-number="forgotMobileNumber"
          :mobile-error="forgotMobileError"
          :loading="loading"
          @update:countryCode="forgotCountryCode = $event"
          @update:mobileNumber="forgotMobileNumber = $event"
          @clear-error="clearForgotMobileError"
          @go-back="goBack"
          @continue="handleForgotPasswordMobile"
        />

        <!-- ============================================ -->
        <!-- 【流程2】忘记密码流程 - 发送验证码页面（从密码输入页面点击） -->
        <!-- ============================================ -->
        <ForgotPasswordVerification
          v-if="currentStep === 'forgot-password-verification'"
          :login-method="loginMethod"
          :email="email"
          :mobile-number="mobileNumber"
          :country-code="countryCode"
          @go-back="goBack"
          @switch-login="handleSwitchLogin"
        />

        <!-- ============================================ -->
        <!-- 【流程2】忘记密码流程 - 输入验证码页面 -->
        <!-- ============================================ -->
        <ForgotPasswordVerifyCode
          v-if="currentStep === 'forgot-password-verify-code'"
          :otp-digits="resetPasswordOtpDigits"
          :otp-error="resetPasswordOtpError"
          :login-method="loginMethod"
          :email="email"
          :mobile-number="mobileNumber"
          :country-code="countryCode"
          :resend-countdown="resetPasswordResendCountdown"
          :loading="loading"
          @update:otpDigits="resetPasswordOtpDigits = $event"
          @clear-error="clearResetPasswordOtpError"
          @go-back="goBack"
          @resend-code="resendResetPasswordOTP"
          @continue="handleForgotPasswordVerifyCode"
        />

        <!-- ============================================ -->
        <!-- 【流程2】重置密码页面 -->
        <!-- ============================================ -->
        <!-- 重置密码表单与成功态合并组件，文案可通过 props 传入 -->
        <ResetPassword
          v-if="currentStep === 'reset-password' || currentStep === 'reset-password-success'"
          :new-password="newPassword"
          :confirm-password="confirmPassword"
          :title="resetPasswordTitle"
          :is-success="currentStep === 'reset-password-success'"
          :success-title="resetPasswordSuccessTitle"
          :success-description="resetPasswordSuccessDesc"
          :success-button-text="resetPasswordSuccessBtn"
          :loading="loading"
          :account-user-id="resetPasswordAccountUserId"
          :login-user="resetPasswordLoginUser"
          @update:newPassword="newPassword = $event"
          @update:confirmPassword="confirmPassword = $event"
          @go-back="goBack"
          @reset-password="handleResetPassword"
          @go-to-login="goToLogin"
        />

        <!-- ============================================ -->
        <!-- 【流程1】忘记密码流程 - 成功页面（手机号） -->
        <!-- ============================================ -->
        <ForgotPasswordSuccess
          v-if="currentStep === 'forgot-password-mobile-success'"
          type="mobile"
          @go-to-login="goToLogin"
        />

        <!-- ============================================ -->
        <!-- 【流程1】忘记密码流程 - 成功页面（邮箱） -->
        <!-- ============================================ -->
        <ForgotPasswordSuccess
          v-if="currentStep === 'forgot-password-email-success'"
          type="email"
          @go-to-login="goToLogin"
        />

        <!-- ============================================ -->
        <!-- 【流程5】忘记用户名流程 - 选项页面 -->
        <!-- ============================================ -->
        <ForgotUsernameOptions
          v-if="currentStep === 'forgot-username-options'"
          @go-back="goBack"
          @switch-login="handleSwitchLogin"
        />

        <!-- ============================================ -->
        <!-- 【流程2】重置密码成功页面 -->
        <!-- ============================================ -->
        <!-- ============================================ -->
        <!-- 【流程5】忘记用户名流程 - 通过手机号找回 -->
        <!-- ============================================ -->
        <ForgotUsernameMobile
          v-if="currentStep === 'forgot-username-mobile'"
          :country-code="forgotUsernameCountryCode"
          :country-options="countryOptions"
          :mobile-number="forgotUsernameMobileNumber"
          :mobile-error="forgotUsernameMobileError"
          :loading="loading"
          @update:countryCode="forgotUsernameCountryCode = $event"
          @update:mobileNumber="forgotUsernameMobileNumber = $event"
          @clear-error="clearForgotUsernameMobileError"
          @go-back="goBack"
          @continue="handleForgotUsernameMobile"
        />

        <!-- ============================================ -->
        <!-- 【流程5】忘记用户名流程 - 通过邮箱找回 -->
        <!-- ============================================ -->
        <ForgotUsernameEmail
          v-if="currentStep === 'forgot-username-email'"
          :email="forgotUsernameEmail"
          :email-error="forgotUsernameEmailError"
          :loading="loading"
          @update:email="forgotUsernameEmail = $event"
          @clear-error="clearForgotUsernameEmailError"
          @go-back="goBack"
          @continue="handleForgotUsername"
        />

        <!-- ============================================ -->
        <!-- 【流程5】忘记用户名流程 - 成功页面（手机号） -->
        <!-- ============================================ -->
        <ForgotUsernameSuccess
          v-if="currentStep === 'forgot-username-mobile-success'"
          type="mobile"
          @go-to-login="goToLogin"
        />

        <!-- ============================================ -->
        <!-- 【流程5】忘记用户名流程 - 成功页面（邮箱） -->
        <!-- ============================================ -->
        <ForgotUsernameSuccess
          v-if="currentStep === 'forgot-username-email-success'"
          type="email"
          @go-to-login="goToLogin"
        />

        <!-- Error Message (后端返回的错误消息，暂时隐藏) -->
        <div v-if="false && errorMessage" class="error-message" style="display: none;">
          {{ errorMessage }}
        </div>

        <!-- Captcha Component (Hidden, triggered programmatically) -->
        <div v-show="false">
          <GeetestCaptcha
            ref="captchaInstance"
            :currentUrlName="GetIsUatName"
            @captchaVerifyCallbackGeetest="finishSlideGeetest"
            @captchaClosed="onLoginCaptchaClosed"
          />
          <!-- ContactUs 专用的验证码组件（Geetest） -->
          <GeetestCaptcha
            ref="contactUsCaptchaInstance"
            :currentUrlName="'contactUs'"
            @captchaVerifyCallbackGeetest="finishContactUsSlideGeetest"
          />
        </div>
      </div>
    </div>

    <!-- ==================== Become Supplier 模态框 ==================== -->
    <!-- ==================== Contact Us 抽屉（包含按钮和表单） ==================== -->
    <ContactUsDrawer
      :show="showContactUsModal"
      :form-data="contactUsForm"
      :reason-options="contactUsReasonOptions"
      :country-options="contactUsCountryOptions"
      :loading="contactUsLoading"
      :submitting="contactUsSubmitting"
      @close="closeContactUsModal"
      @open="toggleContactUsModal"
      @update:formData="handleContactUsFormUpdate"
      @submit="submitContactUs"
    />

    <!-- 模态框组件组 -->
    <div>
      <!-- ContactUs 成功提示弹框 -->
      <ContactUsSuccessModal
        :show="showContactUsSuccessModal"
        @close="closeContactUsSuccessModal"
      />

    </div>
  </div>
</template>

<script>
import language from '../components/header/language.vue';
import GeetestCaptcha from '../components/GeetestCaptcha.vue';
import { mapState } from 'vuex';
import { userService } from '../_services/user.service';
import { CompanyInfoService } from '../_services';
import { parseJwtPayload } from '../_helpers/auth-header';
import Divider from '../components/new-login/Common/Divider.vue';
import LoginHeader from '../components/new-login/Common/LoginHeader.vue';
import BecomeSupplierLink from '../components/new-login/Common/BecomeSupplierLink.vue';
import AlternativeLoginButtons from '../components/new-login/Common/AlternativeLoginButtons.vue';
import UsernameStep from '../components/new-login/LoginSteps/UsernameStep.vue';
import EmailStep from '../components/new-login/LoginSteps/EmailStep.vue';
import MobileStep from '../components/new-login/LoginSteps/MobileStep.vue';
import PasswordStep from '../components/new-login/LoginSteps/PasswordStep.vue';
import OTPStep from '../components/new-login/LoginSteps/OTPStep.vue';
import ForgotPasswordOptions from '../components/new-login/ForgotPassword/ForgotPasswordOptions.vue';
import ForgotPasswordEmail from '../components/new-login/ForgotPassword/ForgotPasswordEmail.vue';
import ForgotPasswordMobile from '../components/new-login/ForgotPassword/ForgotPasswordMobile.vue';
import ForgotPasswordVerification from '../components/new-login/ForgotPassword/ForgotPasswordVerification.vue';
import ForgotPasswordVerifyCode from '../components/new-login/ForgotPassword/ForgotPasswordVerifyCode.vue';
import ResetPassword from '../components/new-login/ForgotPassword/ResetPassword.vue';
import ForgotPasswordSuccess from '../components/new-login/ForgotPassword/ForgotPasswordSuccess.vue';
import ForgotUsernameOptions from '../components/new-login/ForgotUsername/ForgotUsernameOptions.vue';
import ForgotUsernameEmail from '../components/new-login/ForgotUsername/ForgotUsernameEmail.vue';
import ForgotUsernameMobile from '../components/new-login/ForgotUsername/ForgotUsernameMobile.vue';
import ForgotUsernameSuccess from '../components/new-login/ForgotUsername/ForgotUsernameSuccess.vue';
import ContactUsDrawer from '../components/new-login/ContactUs/ContactUsDrawer.vue';
import ContactUsSuccessModal from '../components/new-login/ContactUs/ContactUsSuccessModal.vue';
import newLoginMixin from '../components/new-login/Common/new-login-mixin.js';
import otpMixin from '../components/new-login/Common/otp-mixin.js';
import validationMixin from '../components/new-login/Common/validation-mixin.js';
import { validateEmail, validateMobileWithCountry, findOptionText, handleFormSubmitResult } from '../components/new-login/Common/utils.js';
import {
  DEFAULT_LANG_ID,
  DEFAULT_SHOW_ID,
  DEFAULT_SHOW_CODE,
  getCurrentLangId,
  getContactUsConfig,
  getBecomeSupplierConfig,
  SUBMIT_STATUS_CODE
} from '../components/new-login/Common/new-login-config.js';
import { loadCellCountryOptions } from '../components/new-login/Common/country-loader.js';
import { autoDetectCountryCode } from '../components/new-login/Common/ip-geolocation.js';

export default {
  name: 'Login',
  mixins: [newLoginMixin, otpMixin, validationMixin],
  components: {
    language,
    GeetestCaptcha,
    Divider,
    LoginHeader,
    BecomeSupplierLink,
    AlternativeLoginButtons,
    UsernameStep,
    EmailStep,
    MobileStep,
    PasswordStep,
    OTPStep,
    ForgotPasswordOptions,
    ForgotPasswordEmail,
    ForgotPasswordMobile,
    ForgotPasswordVerification,
    ForgotPasswordVerifyCode,
    ResetPassword,
    ForgotPasswordSuccess,
    ForgotUsernameOptions,
    ForgotUsernameEmail,
    ForgotUsernameMobile,
    ForgotUsernameSuccess,
    ContactUsDrawer,
    ContactUsSuccessModal
  },
  data() {
    return {
      // ==================== 流程控制 ====================
      // 当前步骤：控制显示哪个登录界面
      // 可选值：username, email, mobile, password, otp, 
      //         forgot-password-options, forgot-password-email, forgot-password-mobile,
      //         forgot-password-email-success, forgot-password-mobile-success,
      //         forgot-password-verification, forgot-password-reset-email, forgot-password-reset-mobile,
      //         forgot-password-verify-code, reset-password, reset-password-success,
      //         forgot-username-options, forgot-username-mobile, forgot-username-email,
      //         forgot-username-mobile-success, forgot-username-email-success
      currentStep: 'username',
      
      // 登录方式：username（用户名）, email（邮箱）, mobile（手机号）
      loginMethod: 'username',
      // ==================== 登录表单数据 ====================
      username: '',              // 用户名
      email: '',                 // 邮箱
      mobileNumber: '',          // 手机号
      countryCode: '+86',        // 国家码
      countryOptions: [],        // 国家区号下拉
      password: '',              // 密码
      showPassword: false,       // 是否显示密码
      hasAutofillUsername: false, // 检测用户名输入框是否有浏览器自动填充
      hasAutofillPassword: false, // 检测密码输入框是否有浏览器自动填充
      autofillPollTimer: null, // 轮询检测浏览器/密码管理器填充值（解决不触发 input/change 的情况）
      loginIdentifier: '',       // 登录标识（用于显示）
      otpDigits: ['', '', '', ''], // OTP验证码（4位）
      otpVerifyGuid: '',         // OTP验证码 GUID（发送验证码时返回）
      
      // ==================== 忘记密码相关数据 ====================
      forgotEmail: '',                    // 忘记密码-邮箱
      forgotMobileNumber: '',             // 忘记密码-手机号
      forgotCountryCode: '+86',           // 忘记密码-国家码
      
      // ==================== 忘记用户名相关数据 ====================
      forgotUsernameEmail: '',            // 忘记用户名-邮箱
      forgotUsernameMobileNumber: '',    // 忘记用户名-手机号
      forgotUsernameCountryCode: '+86',  // 忘记用户名-国家码
      
      // ==================== 重置密码相关数据 ====================
      resetPasswordOtpDigits: ['', '', '', ''], // 重置密码验证码
      resetPasswordOtpGuid: '',           // 重置密码验证码 GUID
      resetPasswordSending: false,        // 重置密码发送中
      resetPasswordResendTimer: null,     // 重置密码倒计时计时器
      newPassword: '',                    // 新密码
      confirmPassword: '',                // 确认密码
      resetPasswordResendCountdown: 0,    // 重置密码验证码重发倒计时
      resetPasswordAccountUserId: null,    // 重置密码时的账户用户ID（验证码验证成功后获取）
      resetPasswordLoginUser: '',         // 重置密码时的登录用户名（邮箱或手机号）
      // 重置/修改密码标题文案，可动态覆盖（使用 computed 属性进行三语化）
      
      // ==================== 状态控制 ====================
      loading: false,                     // 加载状态
      isLoginSuccess: false,              // 登录成功加载状态（显示加载动画）
      errorMessage: '',                    // 错误消息
      resendCountdown: 0,                 // 重发倒计时
      resendTimer: null,                  // 重发倒计时计时器
      sendingOtp: false,                  // 发送验证码中标记
      showTooltip: false,                 // 是否显示提示框
      captchaData: null,                  // 验证码数据
      
      // ==================== 错误状态 ====================
      usernameError: false,                // 用户名错误
      emailError: false,                   // 邮箱错误
      mobileError: false,                  // 手机号错误
      passwordError: false,                // 密码错误
      otpError: false,                    // OTP验证码错误
      forgotEmailError: false,             // 忘记密码-邮箱错误
      forgotMobileError: false,            // 忘记密码-手机号错误
      forgotUsernameEmailError: false,     // 忘记用户名-邮箱错误
      forgotUsernameMobileError: false,    // 忘记用户名-手机号错误
      resetPasswordOtpError: false,       // 重置密码验证码错误
      
      // ==================== Become Supplier 模态框 ====================
      // ==================== Contact Us 相关数据 ====================
      showContactUsModal: false,           // 是否显示 Contact Us 模态框
      showContactUsSuccessModal: false,     // 是否显示 Contact Us 成功模态框
      contactUsLoading: false,             // Contact Us 加载状态
      contactUsSubmitting: false,          // Contact Us 提交状态
      contactUsReasonOptions: [],          // Reason 下拉数据（根据 langid 动态过滤）
      contactUsCountryOptions: [],        // Country 下拉数据
      // Reason 配置数据（所有语言版本，不再发送请求）
      contactUsReasonConfig: [
        // 第一类：加入会员
        { value: 1, text: '我想加入成为会员,请联系我。', langid: 936 },
        { value: 1, text: '我想加入成為會員,請聯繫我。', langid: 950 },
        { value: 1, text: 'I am interested to join membership. Please contact me.', langid: 1252 },
        // 第二类：需要协助
        { value: 2, text: '我需要协助,请联系我。', langid: 936 },
        { value: 2, text: '我需要協助,請聯繫我。', langid: 950 },
        { value: 2, text: 'I need assistance. Please contact me.', langid: 1252 }
      ],
      contactUsForm: {
        fid: getContactUsConfig('supplier').fid,
        hidCodepage: String(DEFAULT_LANG_ID),
        showid: null,                      // 展会ID，从路由参数或配置获取，默认使用配置值
        showcode: null,                    // 展会代码，从路由参数或配置获取，默认使用配置值
        langid: DEFAULT_LANG_ID,           // 会在初始化时从 localStorage 获取
        Reason: null,
        ReasonText: '',
        message: '',
        name: '',
        Company: '',
        Country: '',
        CountryText: '',
        Mobile: '',
        Email: '',
        hdPrivacy: false,
        hdShare: true,
        captchaVerifyParam: '',
        submitPageURL: 'VueContactUs', // 页面标识，用于追踪提交来源，会在初始化时从路由获取
        IsNewValidCode: 'true',
        isNotNeedValidate: 'false'
      },
      // 已弃用的 Become Supplier 表单相关数据移除
    };
  },
  computed: {
    ...mapState({
      loggingIn: state => state.authentication.status.loggingIn
    }),
    isValidEmail() {
      return validateEmail(this.email);
    },
    isValidForgotEmail() {
      return validateEmail(this.forgotEmail);
    },
    isValidForgotUsernameEmail() {
      return validateEmail(this.forgotUsernameEmail);
    },
    isMobile() {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    },
    countryCodePlain() {
      // i18n 文案里已包含前缀 '+'（如 "+{countryCode}"），因此这里要去掉可能的 '+'
      return String(this.countryCode || '').replace(/^\+/, '');
    },
    LogoPath() {
      const langid = localStorage.getItem('currentLangId');
      const locale = langid == '936' ? 'sc' : langid == '950' ? 'tc' : 'en';
      const logoEndPoint = `/EMP_logos_${locale}.png?x-oss-process=image/format,webp`;
      return 'https://cdn.adsalecdn.com/EMP/2-0/images' + logoEndPoint;
    },
    GetIsUatName() {
      var isUatName = process.env.VUE_APP_EMP_ENV_NAME == "UAT" ? "eMarketplaceUat_Back" : "eMarketplace_Back";
      isUatName = process.env.VUE_APP_EMP_ENV_NAME == "AI" ? "eMarketplaceAi_Back" : isUatName;
      return isUatName;
    },
    supplierUrl() {
      const langid = localStorage.getItem('currentLangId');
      let lang = 'eng';
      if (langid == '936') {
        lang = 'simp';
      } else if (langid == '950') {
        lang = 'trad';
      }
      return `https://www.chinaplasonline.com/eMarketplace/joinmember/${lang}?regsource=SUPPLIERLANDINGBANNER`;
    },
    resetPasswordTitle() {
      return this.$t('lbl_emp2_ResetPassword');
    },
    resetPasswordSuccessTitle() {
      return this.$t('lbl_emp2_PasswordResetSuccessful');
    },
    resetPasswordSuccessDesc() {
      return this.$t('lbl_emp2_PasswordResetSuccessDesc');
    },
    resetPasswordSuccessBtn() {
      return this.$t('lbl_emp2_BackToLogin');
    }
  },
  mounted() {
    if (typeof window !== 'undefined') {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }
    this.$store.dispatch('alert/clear');

    // 进入登录页时清理旧会话；但如果是带 token 的第三方回调登录，需要先清理再登录，
    // 避免 logout 把刚写入 localStorage 的 token 清掉，导致后续 GetUserInfo 401（缺少 Authorization）
    const tokenFromQuery =
      this.$route.query.Login ||
      this.$route.query.token ||
      this.$route.query.Token ||
      this.$route.query.token;

    //this.$store.dispatch('authentication/logout');
    this.$store.dispatch('authentication/redirectToHomeIfLogin');
    if (tokenFromQuery) {
      this.$store.dispatch('authentication/logout');
      this.submitLoginUsingToken(tokenFromQuery);
    }
    
    // 处理 URL 中的 LogActionType 参数（如果存在）
    // 如果 URL 中有 LogActionType 参数，根据参数值初始化登录方式
    // 如果没有 LogActionType 参数且不是第三方回调登录，默认设置为 'Main'
    const logActionType = this.$route.query.LogActionType;
    if (logActionType) {
      this.initializeLoginTypeFromParam(logActionType);
    } else if (!tokenFromQuery) {
      // 首次访问登录页面时，如果没有 LogActionType 参数，默认设置为 'Main'
      this.updateLogActionTypeParam('Main', true);
    }
    
    // 初始化 Contact Us 的 langid（从 localStorage 获取）
    // 初始化 ContactUs 表单配置
    const currentLangId = localStorage.getItem('currentLangId');
    if (currentLangId) {
      this.contactUsForm.langid = parseInt(currentLangId) || 1252;
      // 确保 hidCodepage 和 langid 一致
      this.contactUsForm.hidCodepage = String(this.contactUsForm.langid);
    }
    
    // 从路由参数获取展会信息（如果路由中有）
    if (this.$route && this.$route.params) {
      if (this.$route.params.showId) {
        this.contactUsForm.showid = parseInt(this.$route.params.showId);
      }
      if (this.$route.params.showCode) {
        this.contactUsForm.showcode = this.$route.params.showCode;
      }
    }
    
    // 如果没有从路由获取到，使用默认值
    const contactUsConfig = getContactUsConfig('supplier');
    if (!this.contactUsForm.showid) {
      this.contactUsForm.showid = contactUsConfig.showid || DEFAULT_SHOW_ID;
    }
    if (!this.contactUsForm.showcode) {
      this.contactUsForm.showcode = contactUsConfig.showcode || DEFAULT_SHOW_CODE;
    }
    
    // 设置页面标识（从路由名称获取，如果没有则使用默认值）
    if (this.$route && this.$route.name) {
      this.contactUsForm.submitPageURL = this.$route.name;
    } else if (!this.contactUsForm.submitPageURL || this.contactUsForm.submitPageURL === 'idx') {
      this.contactUsForm.submitPageURL = 'VueContactUs'; // 默认值
    }
    // 加载登录国家区号
    this.loadCountryOptions();
    
    // 为 body 添加类名，用于隐藏滚动条（兼容不支持 :has() 的浏览器）
    document.body.classList.add('newlogin-hide-scrollbar');
    
    // 检测浏览器自动填充
    this.$nextTick(() => {
      this.checkAutofill();
      setTimeout(() => this.checkAutofill(), 200);
      setTimeout(() => this.checkAutofill(), 500);
      // 某些浏览器/密码管理器会延迟更久才注入值
      setTimeout(() => this.checkAutofill(), 1500);
      setTimeout(() => this.checkAutofill(), 3000);
      // 最稳兜底：短时间轮询同步 DOM value -> v-model
      this.startAutofillPolling();
    });

    // 仅桌面端：尽量触发密码管理器建议（脚本 click 不算用户手势，但 focus 往往能弹建议）
    this.$nextTick(() => this.tryFocusUsernameInput());
  },
  // keep-alive 场景下重新进入页面不会触发 mounted
  activated() {
    this.$nextTick(() => {
      this.tryFocusUsernameInput();
      this.startAutofillPolling();
    });
  },
  methods: {
    // ==================== Tooltip 处理方法 ====================
    
    /**
     * 处理 tooltip 显示（鼠标移入）
     */
    handleTooltipEnter() {
      this.showTooltip = true;
    },
    
    /**
     * 处理 tooltip 隐藏（鼠标移出）
     */
    handleTooltipLeave() {
      this.showTooltip = false;
    },
    
    // ==================== 登录方式切换方法 ====================
    
    /**
     * 统一的登录方式切换处理方法（用于 AlternativeLoginButtons 组件）
     */
    handleSwitchLogin(key) {
      switch (key) {
        case 'username':
          this.switchToUsername();
          break;
        case 'email':
          this.switchToEmail();
          break;
        case 'mobile':
          this.switchToMobile();
          break;
        case 'password':
          this.switchToPassword();
          break;
        case 'otp':
          if (this.loginMethod === 'email' || this.loginMethod === 'mobile') {
            this.showCaptcha(() => this.sendLoginOtpAndEnterOtp());
          } else {
            this.errorMessage = '请先选择邮箱或手机号登录';
          }
          break;
        case 'forgot-username-mobile':
          this.switchToForgotUsernameMobile();
          break;
        case 'forgot-username-email':
          this.switchToForgotUsernameEmail();
          break;
        case 'forgot-password-mobile':
          this.switchToForgotPasswordMobile();
          break;
        case 'forgot-password-email':
          this.switchToForgotPasswordEmail();
          break;
        case 'send-reset-code-email':
          if (this.loginMethod === 'email') {
            this.sendResetPasswordCode();
          } else {
            this.switchToEmailForReset();
          }
          break;
        case 'send-reset-code-mobile':
          if (this.loginMethod === 'mobile') {
            this.sendResetPasswordCode();
          } else {
            this.switchToMobileForReset();
          }
          break;
        case 'contact-us':
          this.handleContactUs();
          break;
        default:
          break;
      }
    },
    /**
     * Become our supplier：直接根据当前语言跳转外部页面
     * 语言映射：936(简体) -> simp, 950(繁体) -> trad, 其他 -> eng
     */
    openBecomeSupplierModal() {
      window.open(this.supplierUrl, '_blank');
    },
    // ==================== 国家区号与验证码辅助方法 ====================
    /**
     * 处理用户手动选择国家代码
     * @param {string} countryCode - 选择的国家代码
     */
    handleCountryCodeUpdate(countryCode) {
      this.countryCode = countryCode;
    },
    async loadCountryOptions() {
      try {
        this.countryOptions = await loadCellCountryOptions();
        
        // 每次页面加载时都根据IP地址自动检测并设置国家代码
        // 注意：只有在 countryCode 是默认值 '+86' 或为空时才自动检测
        // 如果用户已经手动选择了其他值，则保持用户的选择
        const isDefaultValue = this.countryCode === '+86' || !this.countryCode;
        
        if (this.countryOptions.length > 0 && isDefaultValue) {
          // 根据IP地址自动检测国家代码
          const detectedCode = await autoDetectCountryCode(this.countryOptions);
          
          if (detectedCode) {
            // 如果检测到国家代码，使用检测到的代码
            this.countryCode = detectedCode;
            // 同时更新忘记密码和忘记用户名的国家代码
            this.forgotCountryCode = detectedCode;
            this.forgotUsernameCountryCode = detectedCode;
          } else {
            // 如果检测失败，使用第一个选项作为默认值
            this.countryCode = this.countryOptions[0].value;
          }
        }
        
        // 如果countryCode为空且不是默认值，使用第一个选项
        if (this.countryOptions.length > 0 && !this.countryCode) {
          this.countryCode = this.countryOptions[0].value;
        }
      } catch (error) {
        this.countryOptions = [];
        // 如果加载失败，确保有默认值
        if (!this.countryCode || this.countryCode === '+86') {
          this.countryCode = '+86';
        }
      }
    },
    /**
     * 从 countryCode 获取 countryId
     * @param {string} countryCode - 国家码（如 '+86'）
     * @returns {number|null} countryId 或 null
     */
    getCountryIdFromCode(countryCode) {
      if (!countryCode) return null;
      const matched = this.countryOptions.find(opt => opt.value === countryCode);
      return matched?.countryId || null;
    },
    generateGuid() {
      function s4() {
        return Math.floor((1 + Math.random()) * 0x10000)
          .toString(16)
          .substring(1);
      }
      return `${s4()}${s4()}-${s4()}-${4 | s4()}-${5 | s4()}-${s4()}${s4()}${s4()}`;
    },
    getSelectedAreaCode() {
      const pure = (this.countryCode || '').replace('+', '');
      if (pure) return pure;
      const matched = this.countryOptions.find(opt => opt.value === this.countryCode);
      return matched?.area || '';
    },
    /**
     * 根据指定的 countryCode 获取区号（带+号，后端会处理去除+号）
     * @param {string} countryCode - 国家码，例如 "+86" 或 "86"
     * @returns {string} 区号，例如 "+86" 或 "+852"（带+号）
     */
    getSelectedAreaCodeFromCode(countryCode) {
      if (!countryCode) return '';
      // 优先从 countryOptions 中查找，返回带+号的区号
      const matched = this.countryOptions.find(opt => opt.value === countryCode || opt.value === `+${countryCode.replace('+', '')}`);
      if (matched && matched.value) {
        return matched.value; // 返回带+号的区号，例如 "+86" 或 "+852"
      }
      // 如果找不到匹配项，确保返回带+号的格式
      const pure = (countryCode || '').replace('+', '');
      return pure ? `+${pure}` : '';
    },
    /**
     * 发送登录验证码并进入 OTP 页面
     */
    async sendLoginOtpAndEnterOtp() {
      try {
        const res = await this.sendLoginOtp();
        // SendVerification 较慢时也必须等成功后再跳转，避免「无反应 / 再点一次」
        if (res) {
          this.switchToOTP();
        }
        return res;
      } catch (error) {
        this.errorMessage = error?.message || '验证码发送失败，请稍后重试';
        this.otpError = true;
        throw error;
      }
    },
    /**
     * 发送邮箱/手机验证码（登录）
     */
    async sendLoginOtp() {
      if (this.sendingOtp) {
        throw new Error('验证码发送中，请稍候');
      }
      if (this.loginMethod !== 'email' && this.loginMethod !== 'mobile') {
        this.errorMessage = '请选择邮箱或手机号登录';
        throw new Error('invalid login method');
      }
      if (!this.captchaData) {
        this.errorMessage = '请先完成验证码验证';
        throw new Error('captcha required');
      }
      if (this.loginMethod === 'email' && !this.email) {
        this.errorMessage = '请先填写邮箱';
        throw new Error('email required');
      }
      if (this.loginMethod === 'mobile' && !this.mobileNumber) {
        this.errorMessage = '请先填写手机号';
        throw new Error('mobile required');
      }
      this.sendingOtp = true;
      this.loading = true;
      this.errorMessage = '';
      try {
        // 使用原始滑块 typename（保持为 Geetest 类型），默认 CPS_Login
        const captchaParam = {
          ...this.captchaData,
          typename: this.captchaData?.typename || 'CPS_Login'
        };
        let res;
        if (this.loginMethod === 'email') {
          res = await this.sendEmailValidCode(captchaParam);
        } else {
          res = await this.sendMobileValidCode(captchaParam);
        }
        return res;
      } catch (error) {
        this.errorMessage = error?.message || '验证码发送失败，请稍后重试';
        this.otpError = true;
        throw error;
      } finally {
        this.sendingOtp = false;
        this.loading = false;
      }
    },
    async sendEmailValidCode(captchaParam) {
      const langId = parseInt(localStorage.getItem('currentLangId')) || 1252;
      const params = {
        channel: 'email',
        sendType: 'verificationCode',
        typename: captchaParam?.typename || 'CPS_Login',
        lot_number: captchaParam?.lot_number || '',
        captcha_output: captchaParam?.captcha_output || '',
        pass_token: captchaParam?.pass_token || '',
        gen_time: captchaParam?.gen_time || '',
        email: this.email,
        langId: langId
      };
      const res = await userService.sendVerification(params);
      if (!res || !res.isSuccessful) {
        if (res?.statusCode === 1001 || res?.data === '验证码验证失败') {
          this.$swal({
            text: this.$t ? this.$t('lbl_EMPBE_Geetest_Verification') : 'Verification failed',
            showCancelButton: false,
            confirmButtonText: this.$t ? this.$t('lbl_EMPBE_Confirm') : 'OK'
          });
          throw new Error('滑块验证失败');
        }
        const errorMsg = res?.msg?.join('; ') || res?.systemErrorMsg?.join('; ') || '发送邮箱验证码失败';
        throw new Error(errorMsg);
      }
      if (res.data) {
        this.startResendCountdown();
        this.otpVerifyGuid = res.data; // data 是 Guid
        this.otpDigits = ['', '', '', ''];
        this.otpError = false;
        return { status: 200, data: res.data };
      }
      throw new Error('发送邮箱验证码失败：未返回验证码Guid');
    },
    async sendMobileValidCode(captchaParam) {
      const RLang = parseInt(localStorage.getItem('currentLangId')) || 1252;
      // 获取 showid（从配置或默认值）
      const contactUsConfig = getContactUsConfig('supplier');
      const showid = contactUsConfig?.showid || DEFAULT_SHOW_ID || '9991';
      // 获取 countryId 和 countryTelCode（Mobile 渠道建议传递）
      const countryId = this.getCountryIdFromCode(this.countryCode);
      const countryTelCode = this.getSelectedAreaCode(); // 获取区号，例如 "852"
      // 根据 countryId === 44 判断是 CN 还是 OS
      const isInternational = countryId !== 44;
      
      const params = {
        channel: 'mobile',
        sendType: 'verificationCode',
        typename: captchaParam?.typename || 'CPS_Login',
        lot_number: captchaParam?.lot_number || '',
        captcha_output: captchaParam?.captcha_output || '',
        pass_token: captchaParam?.pass_token || '',
        gen_time: captchaParam?.gen_time || '',
        CellNum: this.mobileNumber,
        countryId: countryId, // Mobile 渠道建议传递 countryId
        countryTelCode: countryTelCode, // Mobile 渠道建议传递 countryTelCode（区号，例如 "852"）
        showid: showid.toString(),
        RLang: RLang,
        isInternational: isInternational ? 'true' : 'false'
      };
      const res = await userService.sendVerification(params);
      if (!res || !res.isSuccessful) {
        if (res?.statusCode === 1001 || res?.data === '验证码验证失败') {
          this.$swal({
            text: this.$t ? this.$t('lbl_EMPBE_Geetest_Verification') : 'Verification failed',
            showCancelButton: false,
            confirmButtonText: this.$t ? this.$t('lbl_EMPBE_Confirm') : 'OK'
          });
          throw new Error('滑块验证失败');
        }
        const errorMsg = res?.msg?.join('; ') || res?.systemErrorMsg?.join('; ') || '发送短信验证码失败';
        throw new Error(errorMsg);
      }
      if (res.data) {
        this.startResendCountdown();
        this.otpVerifyGuid = res.data; // data 是 Guid
        this.otpDigits = ['', '', '', ''];
        this.otpError = false;
        return { status: 200, data: res.data };
      }
      throw new Error('发送短信验证码失败：未返回验证码Guid');
    },
    /**
     * 更新 URL 中的 LogActionType 参数用于统计
     * @param {string} loginType - 登录类型: 'accountID', 'email', 'mobile', 'emailOTP', 'mobileOTP'
     * @param {boolean} silent - 是否静默更新（不触发路由更新，避免闪烁）
     */
    updateLoginTypeParam(loginType, silent = false) {
      if (!loginType) return;
      
      // 将 loginType 转换为对应的 LogActionType
      const logActionType = this.getLogActionTypeFromLoginType(loginType);
      
      this.updateLogActionTypeParam(logActionType, silent);
    },
    /**
     * 直接更新 URL 中的 LogActionType 参数
     * @param {string} logActionType - LogActionType 值，如 'Login_Password', 'SendRootAccountForEmail', 'EditLoginUser' 等
     * @param {boolean} silent - 是否静默更新（不触发路由更新，避免闪烁）
     */
    updateLogActionTypeParam(logActionType, silent = false) {
      if (!logActionType) return;
      
      if (silent) {
        // 静默更新：仅基于当前完整 URL 更新 LogActionType，不改变路径，也不触发路由导航
        // 复用统一的 fullPath 逻辑，确保在 UAT 等带有子路径前缀的环境中不会丢失 /eMarketplaceUAT/SupplierCentre 等前缀
        this.updateLogActionTypeParamWithFullPath(logActionType);
      } else {
        // 非静默模式：通过 Vue Router 更新当前路由的 query（会触发导航守卫）
        const query = { ...this.$route.query };
        query.LogActionType = logActionType;
        
        this.$router.replace({
          path: this.$route.path,
          query: query
        }).catch(err => {
          // 忽略导航重复的错误
          if (err.name !== 'NavigationDuplicated') {
            // 静默处理错误
          }
        });
      }
    },
    updateLogActionTypeParamWithFullPath(logActionType) {
      if (!logActionType) return;

      // 基于当前完整 URL，仅更新 LogActionType 查询参数：
      // 1. 不修改 path（保留 /eMarketplaceUAT/SupplierCentre/auth/login 等前缀）
      // 2. 保留所有已有参数，只 set/更新 LogActionType
      // 3. 使用 history.replaceState，避免整页刷新和闪屏
      const url = new URL(window.location.href);
      url.searchParams.set('LogActionType', logActionType);
      window.history.replaceState(null, '', url.toString());
    },
    /**
     * 根据登录方式和验证方式获取 logintype 参数值（用于内部逻辑）
     * @param {string} loginMethod - 登录方式: 'username', 'email', 'mobile'
     * @param {string} verifyMethod - 验证方式: 'password', 'otp'
     * @returns {string} logintype 参数值
     */
    getLoginTypeParam(loginMethod, verifyMethod = 'password') {
      const typeMap = {
        'username': 'accountID',
        'email': verifyMethod === 'otp' ? 'emailOTP' : 'email',
        'mobile': verifyMethod === 'otp' ? 'mobileOTP' : 'mobile'
      };
      return typeMap[loginMethod] || 'accountID';
    },
    /**
     * 根据 logintype 参数值转换为对应的 LogActionType
     * @param {string} loginType - logintype 参数值: 'accountID', 'email', 'mobile', 'emailOTP', 'mobileOTP'
     * @returns {string} LogActionType 值
     */
    getLogActionTypeFromLoginType(loginType) {
      const typeMap = {
        'accountID': 'Login_Password',        // 账号登录（密码登录）
        'email': 'Login_Email_Password',     // 邮箱密码登录
        'mobile': 'Login_Phone_Password',    // 手机号密码登录
        'emailOTP': 'Login_Email_OTP',       // 邮箱验证码登录
        'mobileOTP': 'Login_Phone_OTP'       // 手机号验证码登录
      };
      return typeMap[loginType] || 'Login_Password';
    },
    /**
     * 根据 URL 中的 LogActionType 参数初始化登录方式
     * @param {string} logActionType - URL 中的 LogActionType 参数值
     */
    initializeLoginTypeFromParam(logActionType) {
      // 根据 LogActionType 参数值设置登录方式和步骤
      switch (logActionType) {
        case 'Login_Password':
          // 账号密码登录
          this.switchToUsername();
          break;
        case 'Login_Email_Password':
          // 邮箱密码登录
          this.switchToEmail();
          // 注意：密码步骤需要在验证邮箱后才能进入
          break;
        case 'Login_Email_OTP':
          // 邮箱验证码登录
          this.switchToEmail();
          // 注意：OTP 步骤需要在发送验证码后才能进入
          break;
        case 'Login_Phone_Password':
          // 手机号密码登录
          this.switchToMobile();
          // 注意：密码步骤需要在验证手机号后才能进入
          break;
        case 'Login_Phone_OTP':
          // 手机号验证码登录
          this.switchToMobile();
          // 注意：OTP 步骤需要在发送验证码后才能进入
          break;
        // 兼容旧的LogActionType值
        case 'Login_Email':
          // 兼容：默认当作邮箱验证码登录
          this.switchToEmail();
          break;
        case 'Login_Phone':
          // 兼容：默认当作手机验证码登录
          this.switchToMobile();
          break;
        // 兼容旧的 logintype 参数值
        case 'accountID':
          this.switchToUsername();
          break;
        case 'email':
          this.switchToEmail();
          break;
        case 'mobile':
          this.switchToMobile();
          break;
        case 'emailOTP':
          this.switchToEmail();
          break;
        case 'mobileOTP':
          this.switchToMobile();
          break;
        default:
          // 如果是不认识的 LogActionType 值，保持默认的 username 登录方式
          break;
      }
    },
    /**
     * 切换到用户名登录方式
     */
    switchToUsername() {
      this.currentStep = 'username';
      this.loginMethod = 'username';
      this.clearForm();
      // 更新 URL 参数用于统计
      this.updateLoginTypeParam('accountID');
    },
    /**
     * 切换到邮箱登录方式
     */
    switchToEmail() {
      this.currentStep = 'email';
      this.loginMethod = 'email';
      this.clearForm();
      // 更新 URL 参数用于统计（默认使用密码登录）
      this.updateLoginTypeParam('email');
    },
    /**
     * 切换到手机号登录方式
     */
    switchToMobile() {
      this.currentStep = 'mobile';
      this.loginMethod = 'mobile';
      this.clearForm();
      // 更新 URL 参数用于统计（默认使用密码登录）
      this.updateLoginTypeParam('mobile');
    },
    /**
     * 切换到密码输入步骤（从OTP切换回密码）
     */
    switchToPassword() {
      this.currentStep = 'password';
      // 更新 URL 参数：切换到密码登录
      if (this.loginMethod) {
        const loginType = this.getLoginTypeParam(this.loginMethod, 'password');
        this.updateLoginTypeParam(loginType);
        // 更新 LogActionType（根据登录方式）
        const logActionType = this.getLogActionType(this.loginMethod, false);
        this.updateLogActionTypeParam(logActionType, true);
      }
    },
    /**
     * 切换到OTP验证码输入步骤（从密码切换）
     */
    switchToOTP() {
      this.currentStep = 'otp';
      // 更新 URL 参数：切换到验证码登录
      if (this.loginMethod) {
        const loginType = this.getLoginTypeParam(this.loginMethod, 'otp');
        this.updateLoginTypeParam(loginType);
        // 更新 LogActionType（根据登录方式）
        const logActionType = this.getLogActionType(this.loginMethod, true);
        this.updateLogActionTypeParam(logActionType, true);
      }
      // Auto-focus first OTP input
      this.$nextTick(() => {
        const firstInput = this.$el.querySelector('.otp-input');
        if (firstInput) firstInput.focus();
      });
    },
    /**
     * 切换到忘记密码 - 邮箱方式
     * 只在流程1（从选项页面）中使用，直接发送密码
     */
    switchToForgotPasswordEmail() {
      // 流程1：从选项页面选择，直接发送密码
      this.currentStep = 'forgot-password-email';
      // 更新 LogActionType 为 SendRootPasswordForEmail
      this.updateLogActionTypeParam('SendRootPasswordForEmail', true);
    },
    /**
     * 切换到忘记密码 - 手机号方式
     * 只在流程1（从选项页面）中使用，直接发送密码
     */
    switchToForgotPasswordMobile() {
      // 流程1：从选项页面选择，直接发送密码
      this.currentStep = 'forgot-password-mobile';
      // 更新 LogActionType 为 SendRootPasswordForSMS
      this.updateLogActionTypeParam('SendRootPasswordForSMS', true);
    },
    /**
     * 显示忘记密码界面 - 跳转到选项页面
     */
    /**
     * 显示忘记密码界面
     * 根据登录方式判断：
     * - Username登录 → 显示选项页面（流程1）
     * - Email/Mobile登录 → 直接显示发送验证码页面（流程2）
     */
    showForgotPassword() {
      // 记录当前步骤，用于判断是否从密码页面进入
      this.previousStep = this.currentStep;
      
      // 清空之前的验证码数据，确保忘记密码流程使用新的验证码
      this.captchaData = null;
      this.resetPasswordOtpGuid = '';
      this.resetPasswordOtpDigits = ['', '', '', ''];
      this.resetPasswordOtpError = false;
      this.resetPasswordAccountUserId = null;
      this.resetPasswordLoginUser = '';
      
      // 更新 LogActionType 为 SendRootPassword
      this.updateLogActionTypeParam('SendRootPassword', true);
      
      // 如果是Username登录，显示选项页面（流程1）
      if (this.loginMethod === 'username') {
        this.currentStep = 'forgot-password-options';
      } else {
        // 如果是Email或Mobile登录，直接显示发送验证码页面（流程2）
        this.currentStep = 'forgot-password-verification';
      }
    },
    /**
     * 显示忘记用户名界面 - 跳转到选项页面
     */
    showForgotUsername() {
      // 如果当前在忘记密码流程中，切换到忘记用户名时设置LogActionType为Login_Password
      if (this.currentStep && this.currentStep.startsWith('forgot-password')) {
        this.currentStep = 'forgot-username-options';
        this.updateLogActionTypeParam('Login_Password', true);
      } else {
        // 否则正常进入忘记用户名流程
        this.currentStep = 'forgot-username-options';
        this.updateLogActionTypeParamWithFullPath('SendRootAccount');
      }
    },
    /**
     * 切换到忘记用户名 - 手机号方式
     */
    switchToForgotUsernameMobile() {
      this.resetFieldsForStep('forgot-username-mobile');
      this.currentStep = 'forgot-username-mobile';
      // 更新 LogActionType 为 SendRootAccountForSMS
      this.updateLogActionTypeParam('SendRootAccountForSMS', true);
    },
    /**
     * 切换到忘记用户名 - 邮箱方式
     */
    switchToForgotUsernameEmail() {
      this.resetFieldsForStep('forgot-username-email');
      this.currentStep = 'forgot-username-email';
      // 更新 LogActionType 为 SendRootAccountForEmail
      this.updateLogActionTypeParam('SendRootAccountForEmail', true);
    },
    /**
     * 处理通过手机号找回用户名
     * 直接发送用户名到手机号，无需验证码
     */
    handleForgotUsernameMobile() {
      this.validateAndHandle({
        value: this.forgotUsernameMobileNumber,
        // 使用国家码 + 手机号的统一基础格式校验
        validator: (val) => validateMobileWithCountry(this.forgotUsernameCountryCode, val),
        errorProperty: 'forgotUsernameMobileError',
        onSuccess: () => {
          // 更新 LogActionType 为 SendRootAccountForSMS
          this.updateLogActionTypeParam('SendRootAccountForSMS', true);
          // 先完成滑块验证，然后再发送
          this.showCaptcha(() => {
            this.sendRetrieveCredentialRequest(
              'mobile',
              'username',
              this.forgotUsernameMobileNumber,
              'forgotUsernameMobileError',
              'forgot-username-mobile-success'
            );
          });
        }
      });
    },
    /**
     * 处理联系我们的按钮点击
     * 等同于页面右边的"联系我们"按钮，打开 Contact Us 模态框
     */
    handleContactUs() {
      this.toggleContactUsModal();
    },
    /**
     * 返回上一步（根据当前步骤智能返回）
     */
    goBack() {
      const goTo = (step) => {
        this.resetFieldsForStep(step);
        this.currentStep = step;
      };
      if (this.currentStep === 'password' || this.currentStep === 'otp') {
        if (this.loginMethod === 'username') {
          goTo('username');
        } else if (this.loginMethod === 'email') {
          goTo('email');
        } else if (this.loginMethod === 'mobile') {
          goTo('mobile');
        }
      } else if (this.currentStep === 'forgot-password-email' || this.currentStep === 'forgot-password-mobile') {
        // 从忘记密码的具体方式返回选项页面
        goTo('forgot-password-options');
        // 更新 LogActionType 为 SendRootPassword（选项页面）
        this.updateLogActionTypeParam('SendRootPassword', true);
      } else if (this.currentStep === 'forgot-password-options') {
        // 从忘记密码选项页面返回
        if (this.previousStep === 'password') {
          // 如果是从密码页面进入的，返回到密码页面
          goTo('password');
          this.previousStep = '';
        } else {
          // 否则返回到用户名输入页面
          // 不清空 username，保留用户之前输入的值
          this.currentStep = 'username';
          this.previousStep = '';
          // 更新 LogActionType 为 Main
          this.updateLogActionTypeParam('Main', true);
        }
      } else if (this.currentStep === 'forgot-password-verification') {
        // 从发送验证码页面返回密码输入页面
        goTo('password');
      } else if (this.currentStep === 'forgot-password-reset-email' || this.currentStep === 'forgot-password-reset-mobile') {
        // 从输入邮箱/手机号页面返回发送验证码页面
        goTo('forgot-password-verification');
      } else if (this.currentStep === 'forgot-password-verify-code') {
        // 从输入验证码页面返回发送验证码页面
        goTo('forgot-password-verification');
      } else if (this.currentStep === 'reset-password') {
        // 从重置密码页面返回输入验证码页面
        goTo('forgot-password-verify-code');
      } else if (this.currentStep === 'reset-password-success') {
        // 重置成功页面返回登录
        this.goToLogin();
      } else if (this.currentStep === 'forgot-username-mobile' || this.currentStep === 'forgot-username-email') {
        // 从忘记用户名输入页面返回选项页面
        goTo('forgot-username-options');
        // 更新 LogActionType 为 SendRootAccount（选项页面）
        this.updateLogActionTypeParam('SendRootAccount', true);
      } else if (this.currentStep === 'forgot-username-options') {
        // 从忘记用户名选项页面返回用户名输入页面
        // 不清空 username，保留用户之前输入的值
        this.currentStep = 'username';
        // 更新 LogActionType 为 Main
        this.updateLogActionTypeParam('Main', true);
      } else if (this.currentStep === 'forgot-username-mobile-success' || this.currentStep === 'forgot-username-email-success') {
        // 从成功页面返回登录页面
        goTo('username');
        // 更新 LogActionType 为 Main
        this.updateLogActionTypeParam('Main', true);
      } else if (this.currentStep.startsWith('forgot-')) {
        goTo('username');
        // 更新 LogActionType 为 Main
        this.updateLogActionTypeParam('Main', true);
      }
    },
    // ==================== 流程处理方法 ====================
    
    /**
     * 根据登录方式 + 输入内容计算 LoginType
     * 规则对齐 2026-01-20：Email/Phone 仅走自身；RootLogin 优先，其余 People
     * @param {string} loginMethod - 'username' | 'email' | 'mobile' | ...
     * @param {string} identifier - 用户输入值（用于推断 RootLogin/People/Email/Phone）
     * @returns {string} LoginType 值（RootLogin/People/Email/Phone）
     */
    getLoginType(loginMethod, identifier = '') {
      const value = (identifier || '').trim();
      const method = (loginMethod || '').toLowerCase();

      const inferFromValue = () => {
        if (validateEmail(value)) return 'Email';
        if (/^\d+$/.test(value)) return 'Phone';
        if (/^c/i.test(value)) return 'RootLogin';
        if (value) return 'People';
        return 'RootLogin';
      };

      if (method === 'email') return 'Email';
      if (method === 'mobile' || method === 'phone') return 'Phone';
      if (method === 'root' || method === 'rootlogin') return 'RootLogin';
      if (method === 'people') return 'People';
      // username / all / 其他情况，按输入值推断（ALL 收窄为 RootLogin/People）
      return inferFromValue();
    },
    /**
     * 根据登录方式和验证方式获取 LogActionType
     * @param {string} loginMethod - 登录方式: 'username', 'email', 'mobile'
     * @param {boolean} isVerifyCode - 是否使用验证码登录（true=OTP验证码，false=密码）
     * @returns {string} LogActionType 值
     */
    getLogActionType(loginMethod, isVerifyCode = false) {
      // 根据登录方式和验证方式返回对应的 LogActionType
      if (loginMethod === 'username') {
        return 'Login_Password';  // 账号登录（密码登入）
      } else if (loginMethod === 'email') {
        // 区分Email密码登录和Email验证码登录
        return isVerifyCode ? 'Login_Email_OTP' : 'Login_Email_Password';
      } else if (loginMethod === 'mobile') {
        // 区分Mobile密码登录和Mobile验证码登录
        return isVerifyCode ? 'Login_Phone_OTP' : 'Login_Phone_Password';
      }
      // 默认返回密码登入
      return 'Login_Password';
    },
    /**
     * 【流程1】处理用户名登录 - 点击登录按钮
     * 如果账号和密码都已填写，直接提交登录
     * 只作为 RootLogin/People 通道使用：
     * - 如果输入明显是邮箱/手机号，在前端直接提示用户切到对应登录方式
     */
    /**
     * 【流程1】处理用户名登录 - 点击登录按钮
     * 先验证账号是否存在，账号通过后再验证密码
     */
    handleContinue() {
      // 第一步：验证账号是否填写
      if (!this.username || !this.username.trim()) {
        this.usernameError = true;
        this.passwordError = false; // 清空密码错误
        this.errorMessage = 'Please enter your username';
        return;
      }

      const usernameVal = (this.username || '').trim();

      // 第二步：验证账号格式（前端收窄：账号输入框只给 RootLogin/People 用）
      if (validateEmail(usernameVal)) {
        this.usernameError = true;
        this.passwordError = false; // 清空密码错误
        this.errorMessage = '检测到邮箱格式，请切换到邮箱登录';
        return;
      }
      if (/^\\d+$/.test(usernameVal)) {
        this.usernameError = true;
        this.passwordError = false; // 清空密码错误
        this.errorMessage = '检测到手机号格式，请切换到手机号登录';
        return;
      }

      // 第三步：检查账号是否存在
      this.loading = true;
      this.usernameError = false;
      this.passwordError = false; // 清空密码错误
      this.errorMessage = '';

      (async () => {
        try {
          const loginType = 'RootLogin'; // 明确指定 RootLogin 通道，后端内部再兜 People
          const result = await userService.checkLoginName(usernameVal, loginType);
          
          if (!result.exists) {
            // 账号不存在，显示错误，不检查密码
            this.usernameError = true;
            this.passwordError = false;
            const errorMsg = result?.msg?.join('; ') || 
                           result?.systemErrorMsg?.join('; ') || 
                           result?.message || 
                           '账号不存在，请确认用户名后重试';
            this.errorMessage = errorMsg;
            this.loading = false;
            return;
          }

          // 账号存在，继续验证密码
          this.loginIdentifier = usernameVal;
          this.loginMethod = 'username';
          this.usernameError = false;

          // 第四步：验证密码
          if (!this.password || !this.password.trim()) {
            this.passwordError = true;
            this.errorMessage = 'Please enter your password';
            this.loading = false;
            return;
          }

          // 账号和密码都通过，提交登录
          this.passwordError = false;
          
          // 更新 URL 参数用于统计
          this.updateLoginTypeParam('accountID');
          // 更新 LogActionType 为 Login_Password
          this.updateLogActionTypeParam('Login_Password', true);
          
          // 提交登录（需要先通过滑块验证）
          this.showCaptcha(() => {
            this.submitLogin();
          });
        } catch (error) {
          // 网络错误或其他错误
          this.usernameError = true;
          this.passwordError = false;
          this.errorMessage = '网络异常，请稍后重试';
          this.loading = false;
        }
      })();
    },
    /**
     * 【流程2】处理邮箱登录 - 点击Continue按钮
     * 先验证登录名是否存在，然后进入OTP验证码步骤
     * 使用真实登录逻辑，只验证邮箱格式，不再验证测试账号
     */
    handleEmailContinue() {
      this.validateAndHandle({
        value: this.email,
        validator: (val) => validateEmail(val),
        errorProperty: 'emailError',
        onSuccess: async () => {
          if (this.loading || this.sendingOtp) return;
          // 先验证登录名是否存在
          this.loading = true;
          this.emailError = false;
          let waitingCaptchaSend = false;
          
          try {
            // 传递登录类型信息
            const loginType = this.getLoginType('email', this.email);
            const result = await userService.checkLoginName(this.email.trim(), loginType);
            
            if (result.exists) {
              // 登录名存在，继续到OTP验证码步骤
              this.loginIdentifier = this.email;
              this.loginMethod = 'email';
              this.emailError = false;
              // 更新 LogActionType 为 Login_Email_OTP（邮箱验证码登录）
              this.updateLogActionTypeParam('Login_Email_OTP', true);
              // 拼图完成后等 SendVerification 结束再放行；回调须 return Promise
              waitingCaptchaSend = true;
              this.showCaptcha(() => this.sendLoginOtpAndEnterOtp());
            } else {
              // 登录名不存在，显示错误
              this.emailError = true;
              const errorMsg = result?.msg?.join('; ') || 
                             result?.systemErrorMsg?.join('; ') || 
                             result?.message || 
                             '账号不存在，请确认邮箱后重试';
              this.errorMessage = errorMsg;
            }
          } catch (error) {
            // 网络错误或其他错误
            this.emailError = true;
            this.errorMessage = '网络异常，请稍后重试';
          } finally {
            // 进入拼图/发码流程时保持 loading，避免慢响应期间再次点「继续」
            if (!waitingCaptchaSend) {
              this.loading = false;
            }
          }
        }
      });
    },
    /**
     * 【流程3】处理手机号登录 - 点击Continue按钮
     * 先验证登录名是否存在，然后进入OTP验证码步骤
     * 使用真实登录逻辑，只验证手机号不为空，不再验证测试账号
     */
    handleMobileContinue() {
      this.validateAndHandle({
        value: this.mobileNumber,
        // 使用国家码 + 手机号的统一基础格式校验
        validator: (val) => validateMobileWithCountry(this.countryCode, val),
        errorProperty: 'mobileError',
        onSuccess: async () => {
          if (this.loading || this.sendingOtp) return;
          // 先验证登录名是否存在（手机号格式：国家码+手机号）
          this.loading = true;
          this.mobileError = false;
          let waitingCaptchaSend = false;
          
          try {
            // 使用纯手机号进行验证（不拼接国家码）
            const pureMobileNumber = this.mobileNumber.trim();
            // 传递登录类型信息
            const loginType = this.getLoginType('mobile', pureMobileNumber);
            // 获取 countryId（当 loginType 为 Phone 时）
            const countryId = this.getCountryIdFromCode(this.countryCode);
            const result = await userService.checkLoginName(pureMobileNumber, loginType, countryId);
            
            if (result.exists) {
              // 登录名存在，继续到OTP验证码步骤
              this.loginIdentifier = this.countryCode + ' ' + this.mobileNumber;
              this.loginMethod = 'mobile';
              this.mobileError = false;
              // 更新 LogActionType 为 Login_Phone_OTP（手机验证码登录）
              this.updateLogActionTypeParam('Login_Phone_OTP', true);
              // 拼图完成后等 SendVerification 结束再放行；回调须 return Promise
              waitingCaptchaSend = true;
              this.showCaptcha(() => this.sendLoginOtpAndEnterOtp());
            } else {
              // 登录名不存在，显示错误
              this.mobileError = true;
              const errorMsg = result?.msg?.join('; ') || 
                             result?.systemErrorMsg?.join('; ') || 
                             result?.message || 
                             '账号不存在，请确认手机号后重试';
              this.errorMessage = errorMsg;
            }
          } catch (error) {
            // 网络错误或其他错误
            this.mobileError = true;
            this.errorMessage = '网络异常，请稍后重试';
          } finally {
            // 进入拼图/发码流程时保持 loading，避免慢响应期间再次点「继续」
            if (!waitingCaptchaSend) {
              this.loading = false;
            }
          }
        }
      });
    },
    /**
     * 【通用步骤】处理密码登录
     * 先显示验证码，验证通过后再提交登录
     * 使用真实登录逻辑，只验证密码不为空，不再验证测试密码
     */
    handlePasswordLogin() {
      this.validateAndHandle({
        value: this.password,
        validator: (val) => val && val.trim().length > 0,
        errorProperty: 'passwordError',
        errorMessage: '密码不能为空',
        onSuccess: () => {
          // 对于email和mobile类型，如果之前已经发送过验证码（已经完成滑动验证），则不需要再次显示滑动验证框
          // 因为首次进入email/mobile时已经发过验证码了，已经验证过了
          if (this.loginMethod === 'email' || this.loginMethod === 'mobile') {
            // 直接提交登录，使用之前已经验证过的captchaData
            this.submitLogin();
          } else {
            // 用户名登录需要先通过滑块验证，再提交
            this.showCaptcha(() => {
              this.submitLogin();
            });
          }
        }
      });
    },
    /**
     * 【通用方法】发送找回凭证请求
     * @param {string} loginType - 'email' | 'mobile'
     * @param {string} retrieveType - 'username' | 'password'
     * @param {string} loginValue - 邮箱或手机号
     * @param {string} errorProperty - 错误属性名
     * @param {string} successStep - 成功后的步骤名
     */
    async sendRetrieveCredentialRequest(loginType, retrieveType, loginValue, errorProperty, successStep) {
      this.loading = true;
      this[errorProperty] = false;
      this.errorMessage = '';
      
      try {
        const langId = parseInt(localStorage.getItem("currentLangId")) || 1252;
        const channel = loginType.toLowerCase();

        // 检查是否有滑块验证数据（需要先完成滑块验证）
        if (!this.captchaData) {
          this[errorProperty] = true;
          this.errorMessage = '请先完成验证码验证';
          this.loading = false;
          return;
        }

        // 映射 retrieveType 到 sendType
        const sendType = retrieveType === 'username' ? 'retrieveAccount' : 'retrievePassword';
        let params = {
          channel: channel,
          sendType: sendType,
          typename: this.captchaData?.typename || 'CPS_Login',
          lot_number: this.captchaData?.lot_number || '',
          captcha_output: this.captchaData?.captcha_output || '',
          pass_token: this.captchaData?.pass_token || '',
          gen_time: this.captchaData?.gen_time || '',
          langId: langId
        };
        if (channel === 'email') {
          params.email = loginValue.trim();
        } else {
          const contactUsConfig = getContactUsConfig('supplier');
          const showid = contactUsConfig?.showid || DEFAULT_SHOW_ID || '9991';
          const RLang = langId;
          params.CellNum = loginValue.trim();
          params.showid = showid.toString();
          params.RLang = RLang;
          // 根据 retrieveType 获取正确的 countryCode
          // 找回用户名时使用 forgotUsernameCountryCode，找回密码时使用 forgotCountryCode
          let currentCountryCode = this.countryCode; // 默认使用登录时的 countryCode
          if (retrieveType === 'username') {
            currentCountryCode = this.forgotUsernameCountryCode || this.countryCode;
          } else if (retrieveType === 'password') {
            currentCountryCode = this.forgotCountryCode || this.countryCode;
          }
          // 获取 countryId 和 countryTelCode（Mobile 渠道建议传递）
          const countryId = this.getCountryIdFromCode(currentCountryCode);
          const countryTelCode = this.getSelectedAreaCodeFromCode(currentCountryCode); // 获取区号，例如 "852"
          // 根据 countryId === 44 判断是 CN 还是 OS
          params.isInternational = countryId !== 44 ? 'true' : 'false';
          if (countryId) {
            params.countryId = countryId;
          }
          if (countryTelCode) {
            params.countryTelCode = countryTelCode;
          }
        }
        const result = await userService.sendVerification(params);
        if (result && result.isSuccessful) {
          this.currentStep = successStep;
        } else {
          this[errorProperty] = true;
          const errorMsg = result?.msg?.join('; ') || 
                         result?.systemErrorMsg?.join('; ') || 
                         '发送失败，请稍后重试';
          
          // 检查错误信息是否表示没有管理员用户（仅对找回密码操作）
          if (retrieveType === 'password') {
            const errorMsgLower = errorMsg.toLowerCase();
            const errorMsgArray = result?.msg || result?.systemErrorMsg || [];
            this.errorMessage = errorMsg;
          } else {
            this.errorMessage = errorMsg;
          }
        }
      } catch (error) {
        this[errorProperty] = true;
        this.errorMessage = error.message || '网络异常，请稍后重试';
      } finally {
        this.loading = false;
      }
    },
    /**
     * 【流程1】处理忘记密码 - 邮箱方式
     * 直接发送密码到邮箱（不需要验证码）
     */
    handleForgotPasswordEmail() {
      this.validateAndHandle({
        value: this.forgotEmail,
        validator: (val) => validateEmail(val),
        errorProperty: 'forgotEmailError',
        onSuccess: () => {
          // 更新 LogActionType 为 SendRootPasswordForEmail
          this.updateLogActionTypeParam('SendRootPasswordForEmail', true);
          // 先完成滑块验证，然后再发送
          this.showCaptcha(() => {
            this.sendRetrieveCredentialRequest(
              'email',
              'password',
              this.forgotEmail,
              'forgotEmailError',
              'forgot-password-email-success'
            );
          });
        }
      });
    },
    /**
     * 【流程1】处理忘记密码 - 手机号方式
     * 直接发送密码到手机（不需要验证码）
     */
    handleForgotPasswordMobile() {
      this.validateAndHandle({
        value: this.forgotMobileNumber,
        // 使用国家码 + 手机号的统一基础格式校验
        validator: (val) => validateMobileWithCountry(this.forgotCountryCode, val),
        errorProperty: 'forgotMobileError',
        onSuccess: () => {
          // 更新 LogActionType 为 SendRootPasswordForSMS
          this.updateLogActionTypeParam('SendRootPasswordForSMS', true);
          // 先完成滑块验证，然后再发送
          this.showCaptcha(() => {
            this.sendRetrieveCredentialRequest(
              'mobile',
              'password',
              this.forgotMobileNumber,
              'forgotMobileError',
              'forgot-password-mobile-success'
            );
          });
        }
      });
    },
    /**
     * 【流程2】切换到邮箱方式发送重置密码验证码
     */
    switchToEmailForReset() {
      // 需要用户输入邮箱
      this.currentStep = 'forgot-password-reset-email';
      // 更新 LogActionType 为 EditLoginUser（重置密码）
      this.updateLogActionTypeParam('EditLoginUser', true);
    },
    /**
     * 【流程2】切换到手机号方式发送重置密码验证码
     */
    switchToMobileForReset() {
      // 需要用户输入手机号
      this.currentStep = 'forgot-password-reset-mobile';
      // 更新 LogActionType 为 EditLoginUser（重置密码）
      this.updateLogActionTypeParam('EditLoginUser', true);
    },
    /**
     * 【流程2】发送重置密码验证码到邮箱（从输入邮箱页面）
     */
    sendResetPasswordCodeToEmail() {
      if (!this.isValidEmail) {
        this.errorMessage = 'Please enter a valid email address';
        return;
      }
      // 更新 LogActionType 为 EditLoginUser（重置密码）
      this.updateLogActionTypeParam('EditLoginUser', true);
      this.loginMethod = 'email';
      this.loginIdentifier = this.email;
      this.showCaptcha(() => this.sendResetPasswordCode());
    },
    /**
     * 【流程2】发送重置密码验证码到手机（从输入手机号页面）
     */
    sendResetPasswordCodeToMobile() {
      if (!this.mobileNumber) {
        this.errorMessage = 'Please enter your mobile number';
        return;
      }
      // 更新 LogActionType 为 EditLoginUser（重置密码）
      this.updateLogActionTypeParam('EditLoginUser', true);
      this.loginMethod = 'mobile';
      this.loginIdentifier = this.countryCode + ' ' + this.mobileNumber;
      this.showCaptcha(() => this.sendResetPasswordCode());
    },
    /**
     * 【流程2】发送重置密码验证码
     * 从密码输入页面点击"Forgot Password?"后调用
     */
    sendResetPasswordCode() {
      if (this.resetPasswordSending) return;
      if (this.loginMethod !== 'email' && this.loginMethod !== 'mobile') {
        this.errorMessage = '请选择邮箱或手机号';
        return;
      }
      if (this.loginMethod === 'email' && !this.email) {
        this.errorMessage = '请先填写邮箱';
        return;
      }
      if (this.loginMethod === 'mobile' && !this.mobileNumber) {
        this.errorMessage = '请先填写手机号';
        return;
      }
      // 更新 LogActionType 为 EditLoginUser（重置密码）
      this.updateLogActionTypeParam('EditLoginUser', true);
      // 强制清空旧的验证码数据，确保使用新的验证码
      // 这样可以避免复用登录时的验证码数据
      if (!this.captchaData) {
        this.showCaptcha(() => this.sendResetPasswordCode());
        return;
      }
      this.resetPasswordSending = true;
      this.loading = true;
      this.errorMessage = '';
      const captchaParam = {
        ...this.captchaData,
        typename: this.captchaData?.typename || 'CPS_Login'
      };
      const finish = () => {
        this.resetPasswordSending = false;
        this.loading = false;
      };
      const onSuccess = (guid) => {
        this.resetPasswordOtpGuid = guid;
        this.resetPasswordOtpDigits = ['', '', '', ''];
        this.resetPasswordOtpError = false;
        this.startResetPasswordCountdown();
        this.currentStep = 'forgot-password-verify-code';
      };
      const onError = (msg) => {
        this.errorMessage = msg || '验证码发送失败，请稍后重试';
        this.resetPasswordOtpError = true;
      };
      (async () => {
        try {
          let res;
          if (this.loginMethod === 'email') {
            res = await this.sendResetEmailCode(captchaParam);
          } else {
            res = await this.sendResetMobileCode(captchaParam);
          }
          if (res && (res.status === 200 || res.isSuccessful)) {
            onSuccess(res.data || res?.data);
          } else {
            const errorMsg = res?.msg?.join('; ') || 
                           res?.systemErrorMsg?.join('; ') || 
                           '验证码发送失败，请稍后重试';
            onError(errorMsg);
          }
        } catch (error) {
          onError(error?.message);
        } finally {
          finish();
        }
      })();
    },
    async sendResetEmailCode(captchaParam) {
      const langId = parseInt(localStorage.getItem('currentLangId')) || 1252;
      const params = {
        channel: 'email',
        sendType: 'verificationCode',
        typename: captchaParam?.typename || 'CPS_Login',
        lot_number: captchaParam?.lot_number || '',
        captcha_output: captchaParam?.captcha_output || '',
        pass_token: captchaParam?.pass_token || '',
        gen_time: captchaParam?.gen_time || '',
        email: this.email,
        langId: langId
      };
      const res = await userService.sendVerification(params);
      if (!res || !res.isSuccessful) {
        if (res?.statusCode === 1001 || res?.data === '验证码验证失败') {
          this.$swal({
            text: this.$t ? this.$t('lbl_EMPBE_Geetest_Verification') : 'Verification failed',
            showCancelButton: false,
            confirmButtonText: this.$t ? this.$t('lbl_EMPBE_Confirm') : 'OK'
          });
          throw new Error('滑块验证失败');
        }
        const errorMsg = res?.msg?.join('; ') || res?.systemErrorMsg?.join('; ') || '发送邮箱验证码失败';
        throw new Error(errorMsg);
      }
      return { status: 200, data: res.data };
    },
    async sendResetMobileCode(captchaParam) {
      const RLang = parseInt(localStorage.getItem('currentLangId')) || 1252;
      const contactUsConfig = getContactUsConfig('supplier');
      const showid = contactUsConfig?.showid || DEFAULT_SHOW_ID || '9991';
      // 获取 countryId 和 countryTelCode（Mobile 渠道建议传递）
      const countryId = this.getCountryIdFromCode(this.countryCode);
      const countryTelCode = this.getSelectedAreaCode(); // 获取区号，例如 "852"
      // 根据 countryId === 44 判断是 CN 还是 OS
      const isInternational = countryId !== 44;
      const params = {
        channel: 'mobile',
        sendType: 'verificationCode',
        typename: captchaParam?.typename || 'CPS_Login',
        lot_number: captchaParam?.lot_number || '',
        captcha_output: captchaParam?.captcha_output || '',
        pass_token: captchaParam?.pass_token || '',
        gen_time: captchaParam?.gen_time || '',
        CellNum: this.mobileNumber,
        countryId: countryId, // Mobile 渠道建议传递 countryId
        countryTelCode: countryTelCode, // Mobile 渠道建议传递 countryTelCode（区号，例如 "852"）
        showid: showid.toString(),
        RLang: RLang,
        isInternational: isInternational ? 'true' : 'false'
      };
      const res = await userService.sendVerification(params);
      if (!res || !res.isSuccessful) {
        if (res?.statusCode === 1001 || res?.data === '验证码验证失败') {
          this.$swal({
            text: this.$t ? this.$t('lbl_EMPBE_Geetest_Verification') : 'Verification failed',
            showCancelButton: false,
            confirmButtonText: this.$t ? this.$t('lbl_EMPBE_Confirm') : 'OK'
          });
          throw new Error('滑块验证失败');
        }
        const errorMsg = res?.msg?.join('; ') || res?.systemErrorMsg?.join('; ') || '发送短信验证码失败';
        throw new Error(errorMsg);
      }
      return { status: 200, data: res.data };
    },
    /**
     * 【流程2】处理重置密码验证码输入
     */
    handleResetPasswordOtpInput(index, event) {
      this.handleOtpInputGeneric(
        this.resetPasswordOtpDigits,
        index,
        event,
        () => this.verifyResetPasswordCode(),
        () => this.clearResetPasswordOtpError(),
        '.otp-input',
        this
      );
    },
    /**
     * 【流程2】处理重置密码验证码键盘事件
     */
    handleResetPasswordOtpKeydown(index, event) {
      this.handleOtpKeydownGeneric(
        this.resetPasswordOtpDigits,
        index,
        event,
        '.otp-input',
        this
      );
    },
    /**
     * 【流程2】处理重置密码验证码粘贴
     */
    handleResetPasswordOtpPaste(event) {
      this.handleOtpPasteGeneric(
        this.resetPasswordOtpDigits,
        event,
        () => {
          this.$nextTick(() => {
            this.verifyResetPasswordCode();
          });
        },
        '.otp-input',
        this
      );
    },
    /**
     * 【流程2】验证重置密码验证码
     */
    async verifyResetPasswordCode() {
      const code = this.resetPasswordOtpDigits.join('');
      if (code.length !== 4) {
        this.resetPasswordOtpError = true;
        return;
      }
      if (!this.resetPasswordOtpGuid) {
        this.resetPasswordOtpError = true;
        this.errorMessage = '请先获取验证码';
        return;
      }
      this.resetPasswordOtpError = false;
      this.loading = true;
      try {
        const res = await CompanyInfoService.checkValidCode({
          guid: this.resetPasswordOtpGuid,
          code: code
        });
        if (res && String(res.data).toLowerCase() === 'true') {
          // 验证码验证成功，先记录本次重置密码所对应的账号（邮箱或手机号）
          // 注意：这里不依赖后端返回，只根据当前登录方式来确定
          const loginUser =
            this.loginMethod === 'email'
              ? (this.email || this.loginIdentifier || '')
              : (this.mobileNumber || this.loginIdentifier || '');

          this.resetPasswordLoginUser = loginUser;

          // 如果还没有 AccountUserId，则先设为 0，交由后端按 LoginUser 自行匹配
          if (!this.resetPasswordAccountUserId) {
            this.resetPasswordAccountUserId = 0;
          }

          // 通过 retrievePassword 接口尝试获取 AccountUserId（如果后端有返回的话）
          await this.getResetPasswordAccountInfo();

          // 验证码验证成功后，调用 MARK_VERIFY 标记邮箱/手机已验证
          // 如果有 AccountUserId，则调用标记验证接口
          if (this.resetPasswordAccountUserId && this.resetPasswordAccountUserId > 0) {
            this.markVerificationForResetPassword(this.resetPasswordAccountUserId).catch(() => {
              // 忽略标记验证错误，不影响重置密码流程
            });
          }

          this.currentStep = 'reset-password';
        } else {
          this.resetPasswordOtpError = true;
          const errorMsg = res?.msg?.join('; ') || 
                         res?.systemErrorMsg?.join('; ') || 
                         res?.Msg?.join('; ') ||
                         '验证码错误，请重试';
          this.errorMessage = errorMsg;
        }
      } catch (error) {
        this.resetPasswordOtpError = true;
        this.errorMessage = error?.message || '验证码验证失败';
      } finally {
        this.loading = false;
      }
    },
    /**
     * 【流程2】处理忘记密码验证码继续按钮点击
     */
    handleForgotPasswordVerifyCode() {
      this.verifyResetPasswordCode();
    },
    /**
     * 【流程2】重新发送重置密码验证码
     */
    resendResetPasswordOTP() {
      if (this.resetPasswordResendCountdown > 0) return;
      this.showCaptcha(() => this.sendResetPasswordCode());
    },
    startResetPasswordCountdown() {
      this.resetPasswordResendCountdown = 60;
      if (this.resetPasswordResendTimer) {
        clearInterval(this.resetPasswordResendTimer);
      }
      this.resetPasswordResendTimer = setInterval(() => {
        this.resetPasswordResendCountdown--;
        if (this.resetPasswordResendCountdown <= 0) {
          clearInterval(this.resetPasswordResendTimer);
          this.resetPasswordResendTimer = null;
        }
      }, 1000);
    },
    /**
     * 【流程2】处理重置密码
     */
    async handleResetPassword(payload = {}) {
      const newPwd = payload.newPassword ?? this.newPassword;
      const confirmPwd = payload.confirmPassword ?? this.confirmPassword;

      this.newPassword = newPwd;
      this.confirmPassword = confirmPwd;

      this.updateLogActionTypeParam('EditLoginUser', true);
      if (!newPwd) {
        this.errorMessage = 'Please enter a new password';
        return;
      }
      if (!confirmPwd) {
        this.errorMessage = 'Please confirm your password';
        return;
      }
      if (newPwd !== confirmPwd) {
        this.errorMessage = 'Passwords do not match';
        return;
      }
      if (newPwd.length < 6) {
        this.errorMessage = this.$t('EMP_UserSetting_PasswordStrengthTip');
        return;
      }
      this.loading = true;
      this.errorMessage = '';
      
      try {
        // 获取 AccountUserId 和用户名
        const accountUserId = this.resetPasswordAccountUserId;
        const loginUser = this.resetPasswordLoginUser || this.email || this.mobileNumber || '';

        // 现在后端允许 AccountUserId 为空/0，只要有 loginUser 即可
        // 仅当 AccountUserId 和 loginUser 都缺失时才提示错误
        if ((!accountUserId || accountUserId <= 0) && !loginUser) {
          this.errorMessage = 'User account information not found. Please complete the verification process.';
          this.loading = false;
          return;
        }

        // 调用 API 更新密码
        const response = await userService.UpPasswordOrIsVerifyByAccountUserId({
          actionType: 'UPDATE_PASSWORD',
          accountUserId: accountUserId,
          newPassword: newPwd,
          loginUser: loginUser
        });

        if (response && response.isSuccessful) {
          // 密码更新成功
          this.currentStep = 'reset-password-success';
        } else {
          // 密码更新失败
          const errorMsg = response?.msg?.join('; ') || 
                          response?.systemErrorMsg?.join('; ') || 
                          'Password update failed. Please try again.';
          this.errorMessage = errorMsg;
        }
      } catch (error) {
        this.errorMessage = error.message || 'An error occurred. Please try again.';
      } finally {
        this.loading = false;
      }
    },
    /**
     * 获取重置密码时的账户信息（AccountUserId）
     * 通过 retrievePassword 接口获取
     */
    async getResetPasswordAccountInfo() {
      try {
        // 使用 retrievePassword 类型的 sendVerification 接口获取 AccountUserId
        const captchaParam = this.captchaData || {};
        const langId = parseInt(localStorage.getItem('currentLangId')) || 1252;
        
        let params;
        if (this.loginMethod === 'email') {
          params = {
            channel: 'email',
            sendType: 'retrievePassword', // 使用 retrievePassword 类型
            typename: captchaParam?.typename || 'CPS_Login',
            lot_number: captchaParam?.lot_number || '',
            captcha_output: captchaParam?.captcha_output || '',
            pass_token: captchaParam?.pass_token || '',
            gen_time: captchaParam?.gen_time || '',
            email: this.email,
            langId: langId
          };
        } else {
          const RLang = langId;
          const contactUsConfig = getContactUsConfig('supplier');
          const showid = contactUsConfig?.showid || DEFAULT_SHOW_ID || '9991';
          // 获取 countryId 和 countryTelCode（Mobile 渠道建议传递）
          const countryId = this.getCountryIdFromCode(this.countryCode);
          const countryTelCode = this.getSelectedAreaCode(); // 获取区号，例如 "852"
          // 根据 countryId === 44 判断是 CN 还是 OS
          const isInternational = countryId !== 44;
          params = {
            channel: 'mobile',
            sendType: 'retrievePassword', // 使用 retrievePassword 类型
            typename: captchaParam?.typename || 'CPS_Login',
            lot_number: captchaParam?.lot_number || '',
            captcha_output: captchaParam?.captcha_output || '',
            pass_token: captchaParam?.pass_token || '',
            gen_time: captchaParam?.gen_time || '',
            CellNum: this.mobileNumber,
            countryId: countryId, // Mobile 渠道建议传递 countryId
            countryTelCode: countryTelCode, // Mobile 渠道建议传递 countryTelCode（区号，例如 "852"）
            showid: showid.toString(),
            RLang: RLang,
            isInternational: isInternational ? 'true' : 'false'
          };
        }

        const res = await userService.sendVerification(params);
        
        // 如果接口返回 AccountUserId，保存它
        if (res && res.isSuccessful && res.data) {
          // 根据实际返回数据结构调整
          // 如果返回的是 AccountUserId，直接使用
          if (res.data.AccountUserId || res.data.accountUserId) {
            this.resetPasswordAccountUserId = res.data.AccountUserId || res.data.accountUserId;
            this.resetPasswordLoginUser = this.loginMethod === 'email' ? this.email : this.mobileNumber;
          }
          // 如果返回的是对象，尝试获取 AccountUserId
          else if (typeof res.data === 'object') {
            this.resetPasswordAccountUserId = res.data.AccountUserId || res.data.accountUserId || res.data.AccountId || res.data.accountId;
            this.resetPasswordLoginUser = this.loginMethod === 'email' ? this.email : this.mobileNumber;
          }
        }
      } catch (error) {
        // 如果获取失败，尝试使用验证码 GUID 从后端获取
        // 这里可以根据实际后端接口调整
      }
    },
    /**
     * 【流程5】处理忘记用户名 - 通过邮箱
     * 直接发送用户名到邮箱，无需验证码
     */
    handleForgotUsername() {
      this.validateAndHandle({
        value: this.forgotUsernameEmail,
        validator: (val) => validateEmail(val),
        errorProperty: 'forgotUsernameEmailError',
        onSuccess: () => {
          // 更新 LogActionType 为 SendRootAccountForEmail
          this.updateLogActionTypeParam('SendRootAccountForEmail', true);
          // 先完成滑块验证，然后再发送
          this.showCaptcha(() => {
            this.sendRetrieveCredentialRequest(
              'email',
              'username',
              this.forgotUsernameEmail,
              'forgotUsernameEmailError',
              'forgot-username-email-success'
            );
          });
        }
      });
    },
    /**
     * 返回登录页面
     */
    goToLogin() {
      this.clearForm();
      this.currentStep = 'username';
    },
    // ==================== OTP验证码相关方法 ====================
    
    /**
     * 处理OTP输入框输入事件
     * @param {number} index - 当前输入框索引
     * @param {Event} event - 输入事件
     */
    handleOtpInput(index, event) {
      this.handleOtpInputGeneric(
        this.otpDigits,
        index,
        event,
        () => this.submitOTP(),
        () => this.clearOtpError(),
        '.otp-input',
        this
      );
    },
    /**
     * 处理OTP输入框键盘事件（支持Backspace回退）
     * @param {number} index - 当前输入框索引
     * @param {Event} event - 键盘事件
     */
    handleOtpKeydown(index, event) {
      this.handleOtpKeydownGeneric(
        this.otpDigits,
        index,
        event,
        '.otp-input',
        this
      );
    },
    /**
     * 处理OTP输入框粘贴事件（支持一次性粘贴4位数字）
     * @param {Event} event - 粘贴事件
     */
    handleOtpPaste(event) {
      // 先立即显示验证码（通过 handleOtpPasteGeneric 更新 otpDigits）
      this.handleOtpPasteGeneric(
        this.otpDigits,
        event,
        () => {
          // 验证码已显示，立即显示 loading 状态
          this.otpError = false;
          this.errorMessage = '';
          this.loading = true;
          
          // 延迟500毫秒后再验证准确性
          setTimeout(() => {
            // 临时标记，允许 submitOTP 在 loading 状态下继续执行
            this._isPasteTriggered = true;
            this.submitOTP();
            this._isPasteTriggered = false;
          }, 500);
        },
        '.otp-input',
        this
      );
    },
    /**
     * 提交OTP验证码进行验证
     * 使用 newLogin 接口进行登录，传递 isVerifyCode: true
     */
    async submitOTP() {
      const otp = this.otpDigits.join('');
      
      if (otp.length !== 4) {
        this.loading = false;
        this.otpError = true;
        this.errorMessage = '验证码不完整';
        return;
      }
      
      // 如果是粘贴触发的，允许在 loading 状态下继续执行
      if (this.loading && !this._isPasteTriggered) {
        return;
      }
      
      // 如果不是粘贴触发的，设置 loading 状态
      if (!this._isPasteTriggered) {
        this.loading = true;
      }
      this.otpError = false;
      this.errorMessage = '';
      
      if (!this.otpVerifyGuid) {
        this.loading = false;
        this.otpError = true;
        this.errorMessage = '请先获取验证码';
        return;
      }
      
      const isValid = await this.verifyOtpCode(otp);
      
      if (!isValid) {
        this.loading = false;
        this.otpError = true;
        this.errorMessage = '验证码错误，请重试';
        return;
      }
      
      // 根据登录方式构建 username 参数
      let username = '';
      if (this.loginMethod === 'email') {
        username = this.email;
      } else if (this.loginMethod === 'mobile') {
        username = this.mobileNumber;
      }
      
      // 根据登录方式+输入计算 loginType（Email/Phone）
      const loginType = this.getLoginType(this.loginMethod, username);
      
      // 获取 countryId（当 loginType 为 Phone 时）
      const countryId = loginType === 'Phone' ? this.getCountryIdFromCode(this.countryCode) : null;
      
      // Geetest 验证码参数（email/mobile 登录时，在进入 OTP 步骤前已经完成滑块验证）
      const typename = this.captchaData?.typename || 'CPS_Login';
      const lot_number = this.captchaData?.lot_number || '';
      const captcha_output = this.captchaData?.captcha_output || '';
      const pass_token = this.captchaData?.pass_token || '';
      const gen_time = this.captchaData?.gen_time || '';
      
      // OTP 验证码参数（使用验证码登录时必需）
      const verifyGuid = this.otpVerifyGuid || '';
      const verifyCode = otp;
      
      // 判断是否使用验证码登录（email/mobile 使用验证码登录时为 true）
      const isVerifyCode = true;
      
      // 更新 LogActionType 参数（根据登录方式）
      const logActionType = this.getLogActionType(this.loginMethod, true);
      this.updateLogActionTypeParam(logActionType, true);
      
      // 调用 newLogin 接口进行登录
      userService.newLogin(
        username,
        '', // 使用验证码登录时，密码可以为空
        loginType,
        typename,
        lot_number,
        captcha_output,
        pass_token,
        gen_time,
        verifyGuid,
        verifyCode,
        isVerifyCode,
        countryId
      ).then(res => {
        this.loading = false;
        
        // 添加 null 检查，防止 "Cannot read properties of null" 错误
        if (!res) {
          this.otpError = true;
          this.errorMessage = '网络异常，请稍后重试';
          return;
        }
        
        if (res.statusCode == 200 && res.isSuccessful) {
          // 显示登录成功加载动画
          this.isLoginSuccess = true;
          
          // 登录成功，更新 store 状态
          const { commit, menu } = this.$store;
          commit && commit('authentication/loginSuccess', res.data);
          
          // 验证码验证成功后，调用 MARK_VERIFY 标记邮箱/手机已验证
          // 注意：这里使用异步调用，不阻塞登录流程
          if (res.data) {
            // 优先从 res.data 中直接获取 AccountUserId
            let accountUserId = res.data.AccountUserId || res.data.accountUserId || res.data.AccountId || res.data.accountId;
            
            // 如果 res.data 中没有 AccountUserId，尝试从 token 中解析
            if (!accountUserId && res.data.token) {
              try {
                const jwtPayload = parseJwtPayload(res.data.token);
                if (jwtPayload) {
                  // 尝试多种可能的字段名
                  accountUserId = jwtPayload.accountUserId || jwtPayload.accountUserid || jwtPayload.AccountUserId || jwtPayload.AccountUserid;
                }
              } catch (error) {
                // 忽略解析错误
              }
            }
            
            // 确保 accountUserId 是数字类型
            if (accountUserId) {
              accountUserId = parseInt(accountUserId);
              if (isNaN(accountUserId)) {
                accountUserId = null;
              }
            }
            
            if (accountUserId && accountUserId > 0) {
              this.markVerificationAfterOtpSuccess(accountUserId).catch(() => {
                // 忽略标记验证错误，不影响登录流程
              });
            }
          }
          
          // 延迟跳转，让用户看到加载动画
          setTimeout(() => {
            const query = this.$route.query || {};
            if (res.data && res.data.userType == 'ADMIN') {
              const adminMenu = this.$store && this.$store.menu;
              adminMenu?.mutations?.loginAsAdmin?.(adminMenu.state);
              this.$router.push({
                path: '/Management/supplier',
                query: query
              }).catch(() => {});
            } else {
              // KYB 锁定改道时 Vue Router 会 reject，忽略即可
              this.$router.push({
                path: '/',
                query: query
              }).catch(() => {});
            }
          }, 500); // 显示加载动画 500ms 后跳转
        } else if (res.statusCode == 1001) {
          this.$swal({
            text: this.$t ? this.$t('lbl_EMPBE_Geetest_Verification') : 'Verification failed',
            showCancelButton: false,
            confirmButtonText: this.$t ? this.$t('lbl_EMPBE_Confirm') : 'OK'
          });
        } else {
          // 处理登录失败：检查 statusCode == 999 或其他错误情况
          this.otpError = true;
          const errorMsg = res?.msg?.join('; ') || 
                         res?.systemErrorMsg?.join('; ') || 
                         res?.Msg?.join('; ') ||
                         res?.data?.message ||
                         '验证码错误，请重试';
          this.errorMessage = errorMsg;
        }
      }).catch(error => {
        this.loading = false;
        this.otpError = true;
        this.errorMessage = error.message || '网络异常，请稍后重试';
      });
    },
    async verifyOtpCode(code) {
      try {
        const res = await CompanyInfoService.checkValidCode({
          guid: this.otpVerifyGuid,
          code: code
        });
        return res && String(res.data).toLowerCase() === 'true';
      } catch (error) {
        return false;
      }
    },
    /**
     * 登录成功后标记验证（MARK_VERIFY）
     * @param {number} accountUserId - 用户账户ID
     */
    async markVerificationAfterOtpSuccess(accountUserId) {
      if (!accountUserId || accountUserId <= 0) {
        return;
      }

      // 根据登录方式确定 loginUser、loginType 和 countryId
      let loginUser = '';
      let loginType = '';
      let countryId = null;
      
      if (this.loginMethod === 'email') {
        loginUser = this.email || '';
        loginType = 'Email';
      } else if (this.loginMethod === 'mobile') {
        loginUser = this.mobileNumber || '';
        loginType = 'Phone';
        countryId = this.getCountryIdFromCode(this.countryCode);
      } else {
        return;
      }

      if (!loginUser) {
        return;
      }

      try {
        const params = {
          actionType: 'MARK_VERIFY',
          accountUserId: accountUserId,
          loginUser: loginUser,
          countryId: countryId || 0
        };

        await userService.UpPasswordOrIsVerifyByAccountUserId(params);
      } catch (error) {
        // 忽略标记验证错误，不影响登录流程
      }
    },
    /**
     * 重置密码验证成功后标记验证（MARK_VERIFY）
     * @param {number} accountUserId - 用户账户ID
     */
    async markVerificationForResetPassword(accountUserId) {
      if (!accountUserId || accountUserId <= 0) {
        return;
      }

      // 根据登录方式确定 loginUser、loginType 和 countryId
      let loginUser = '';
      let loginType = '';
      let countryId = null;

      if (this.loginMethod === 'email') {
        loginUser = this.email || this.loginIdentifier || this.resetPasswordLoginUser || '';
        loginType = 'Email';
      } else if (this.loginMethod === 'mobile') {
        loginUser = this.mobileNumber || this.loginIdentifier || this.resetPasswordLoginUser || '';
        loginType = 'Phone';
        countryId = this.getCountryIdFromCode(this.countryCode);
      } else {
        return;
      }

      if (!loginUser) {
        return;
      }

      try {
        const params = {
          actionType: 'MARK_VERIFY',
          accountUserId: accountUserId,
          loginUser: loginUser,
          countryId: countryId || 0
        };

        await userService.UpPasswordOrIsVerifyByAccountUserId(params);
      } catch (error) {
        // 忽略标记验证错误，不影响重置密码流程
      }
    },
    /**
     * 重新发送OTP验证码
     */
    resendOTP() {
      if (this.resendCountdown > 0) return;
      if (this.loginMethod !== 'email' && this.loginMethod !== 'mobile') return;
      this.showCaptcha(() => this.sendLoginOtp());
    },
    /**
     * 开始重发倒计时（60秒）
     */
    startResendCountdown() {
      this.resendCountdown = 60;
      if (this.resendTimer) {
        clearInterval(this.resendTimer);
      }
      this.resendTimer = setInterval(() => {
        this.resendCountdown--;
        if (this.resendCountdown <= 0) {
          clearInterval(this.resendTimer);
          this.resendTimer = null;
        }
      }, 1000);
    },
    // ==================== 辅助方法 ====================
    
    /**
     * 切换密码显示/隐藏
     */
    togglePassword() {
      this.showPassword = !this.showPassword;
    },
    /**
     * 触发浏览器保存密码提示
     * 通过创建一个隐藏的表单并提交，让浏览器识别这是一个登录表单
     */
    triggerBrowserPasswordSave() {
      try {
        // 获取用户名（根据登录方式）
        let usernameValue = '';
        if (this.loginMethod === 'username') {
          usernameValue = this.username;
        } else if (this.loginMethod === 'email') {
          usernameValue = this.email;
        } else if (this.loginMethod === 'mobile') {
          usernameValue = this.countryCode + this.mobileNumber;
        }
        
        if (!usernameValue || !this.password) {
          return;
        }
        
        // 创建一个隐藏的表单
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = window.location.href;
        form.style.position = 'absolute';
        form.style.left = '-9999px';
        form.style.top = '-9999px';
        form.style.opacity = '0';
        form.style.pointerEvents = 'none';
        
        // 创建用户名输入框
        const usernameInput = document.createElement('input');
        usernameInput.type = 'text';
        usernameInput.name = 'username';
        usernameInput.value = usernameValue;
        usernameInput.autocomplete = 'username';
        form.appendChild(usernameInput);
        
        // 创建密码输入框
        const passwordInput = document.createElement('input');
        passwordInput.type = 'password';
        passwordInput.name = 'password';
        passwordInput.value = this.password;
        passwordInput.autocomplete = 'current-password';
        form.appendChild(passwordInput);
        
        // 添加到页面
        document.body.appendChild(form);
        
        // 提交表单（这会触发浏览器的密码保存提示）
        // 注意：由于我们阻止了默认行为，表单不会真正提交
        form.addEventListener('submit', (e) => {
          e.preventDefault();
          e.stopPropagation();
        });
        
        // 触发提交事件
        const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
        form.dispatchEvent(submitEvent);
        
        // 延迟移除表单，给浏览器足够的时间识别
        setTimeout(() => {
          if (form.parentNode) {
            form.parentNode.removeChild(form);
          }
        }, 1000);
      } catch (error) {
        // 静默处理浏览器密码保存失败
      }
    },
    clearError(errorProperty) {
      if (errorProperty) {
        this[errorProperty] = false;
      } else {
        this.errorMessage = '';
        this.usernameError = false;
      }
    },
    clearUsernameError() {
      this.clearError('usernameError');
    },
    handleUsernameFocus(event) {
      this.checkInputAutofill(event.target, 'username');
      this.clearError();
    },
    handleUsernameEnter() {
      if (!this.loading && this.username && this.username.trim().length > 0) {
        this.handleContinue();
      }
    },
    handleUsernameInput(event) {
      const value = event.target.value.replace(/\s/g, '');
      this.username = value;
      // 如果输入框有值，清除自动填充标记（因为现在是用户输入）
      if (value) {
        this.hasAutofillUsername = false;
      }
      this.clearUsernameError();
    },
    clearEmailError() {
      this.clearError('emailError');
    },
    handleEmailEnter() {
      if (!this.loading && this.isValidEmail) {
        this.handleEmailContinue();
      }
    },
    handleEmailInput(event) {
      const value = event.target.value.replace(/\s/g, '');
      this.email = value;
      this.clearEmailError();
    },
    clearMobileError() {
      this.clearError('mobileError');
    },
    clearPasswordError() {
      this.clearError('passwordError');
    },
    handlePasswordFocus(event) {
      this.checkInputAutofill(event.target, 'password');
      this.clearPasswordError();
    },
    handlePasswordInput(event) {
      const value = event.target.value.replace(/\s/g, '');
      this.password = value;
      if (value) {
        this.hasAutofillPassword = false;
      }
      this.clearPasswordError();
    },
    checkInputAutofill(inputElement, type) {
      if (!inputElement) return;
      
      const inputValue = inputElement.value;
      const vueValue = type === 'username' ? this.username : this.password;
      const isAutofilled = inputValue && inputValue.trim() && !vueValue;
      
      if (type === 'username') {
        this.hasAutofillUsername = isAutofilled;
        if (isAutofilled && inputValue && !this.username) {
          this.username = inputValue;
        }
      } else if (type === 'password') {
        this.hasAutofillPassword = isAutofilled;
        if (isAutofilled && inputValue && !this.password) {
          this.password = inputValue;
        }
      }
    },
    checkAutofill() {
      if (this.currentStep !== 'username') return;
      
      if (this.$refs.usernameInput) {
        this.checkInputAutofill(this.$refs.usernameInput, 'username');
      }
      if (this.$refs.passwordInput) {
        this.checkInputAutofill(this.$refs.passwordInput, 'password');
      }
    },
    /**
     * 短时间轮询同步：兼容浏览器 autofill / 1Password 等直接写 value 但不触发事件
     */
    startAutofillPolling() {
      // 只在 username step 轮询，避免其他步骤浪费
      if (this.currentStep !== 'username') return;
      this.stopAutofillPolling();

      let ticks = 0;
      this.autofillPollTimer = setInterval(() => {
        ticks += 1;
        this.checkAutofill();

        // 两个字段都同步到了就提前停止；最多 8 秒
        if ((this.username && this.password) || ticks >= 32) {
          this.stopAutofillPolling();
        }
      }, 250);
    },
    stopAutofillPolling() {
      if (this.autofillPollTimer) {
        clearInterval(this.autofillPollTimer);
        this.autofillPollTimer = null;
      }
    },
    /**
     * 仅桌面端：尝试聚焦账号输入框，便于触发密码管理器建议
     */
    tryFocusUsernameInput() {
      try {
        if (this.isMobile) return;
        if (this.currentStep !== 'username') return;
        // 如果已经有值，就不抢焦点
        if (this.username || this.password) return;
        const el = this.$refs && this.$refs.usernameInput;
        if (!el || typeof el.focus !== 'function') return;
        const active = document.activeElement;
        if (active && active !== document.body && active !== document.documentElement) return;
        el.focus();
      } catch (e) {
        // ignore
      }
    },
    clearOtpError() {
      this.clearError('otpError');
    },
    clearForgotEmailError() {
      this.clearError('forgotEmailError');
    },
    clearForgotMobileError() {
      this.clearError('forgotMobileError');
    },
    clearForgotUsernameEmailError() {
      this.clearError('forgotUsernameEmailError');
    },
    clearForgotUsernameMobileError() {
      this.clearError('forgotUsernameMobileError');
    },
    clearResetPasswordOtpError() {
      this.clearError('resetPasswordOtpError');
    },
    /**
     * 清空表单数据
     */
    clearForm() {
      this.username = '';
      this.email = '';
      this.mobileNumber = '';
      this.password = '';
      this.otpDigits = ['', '', '', ''];
      this.otpVerifyGuid = '';
      if (this.resendTimer) {
        clearInterval(this.resendTimer);
        this.resendTimer = null;
      }
      this.resendCountdown = 0;
      this.resetPasswordOtpDigits = ['', '', '', ''];
      this.resetPasswordOtpGuid = '';
      if (this.resetPasswordResendTimer) {
        clearInterval(this.resetPasswordResendTimer);
        this.resetPasswordResendTimer = null;
      }
      this.resetPasswordResendCountdown = 0;
      this.newPassword = '';
      this.confirmPassword = '';
      this.forgotEmail = '';
      this.forgotMobileNumber = '';
      this.forgotUsernameEmail = '';
      this.forgotUsernameMobileNumber = '';
      this.forgotCountryCode = '+86';
      this.forgotUsernameCountryCode = '+86';
      this.errorMessage = '';
      this.usernameError = false;
      this.emailError = false;
      this.mobileError = false;
      this.passwordError = false;
      this.otpError = false;
      this.forgotEmailError = false;
      this.forgotMobileError = false;
      this.forgotUsernameEmailError = false;
      this.forgotUsernameMobileError = false;
      this.resetPasswordOtpError = false;
    },
    /**
     * 根据目标步骤清空对应表单字段
     * 使用配置驱动的方式，减少重复代码
     * @param {string} step
     */
    resetFieldsForStep(step) {
      // 字段映射配置：step -> [{ field, value, errorField }]
      const stepFieldMap = {
        'username': [
          { field: 'username', value: '', errorField: 'usernameError' }
        ],
        'email': [
          { field: 'email', value: '', errorField: 'emailError' }
        ],
        'mobile': [
          { field: 'mobileNumber', value: '', errorField: 'mobileError' }
        ],
        'password': [
          { field: 'password', value: '', errorField: 'passwordError' }
        ],
        'otp': [
          { field: 'otpDigits', value: ['', '', '', ''], errorField: 'otpError' }
        ],
        'forgot-password-email': [
          { field: 'forgotEmail', value: '', errorField: 'forgotEmailError' }
        ],
        'forgot-password-mobile': [
          { field: 'forgotMobileNumber', value: '', errorField: 'forgotMobileError' },
          { field: 'forgotCountryCode', value: '+86', errorField: null }
        ],
        'forgot-username-mobile': [
          { field: 'forgotUsernameMobileNumber', value: '', errorField: 'forgotUsernameMobileError' },
          { field: 'forgotUsernameCountryCode', value: '+86', errorField: null }
        ],
        'forgot-username-email': [
          { field: 'forgotUsernameEmail', value: '', errorField: 'forgotUsernameEmailError' }
        ],
        'forgot-password-verify-code': [
          { field: 'resetPasswordOtpDigits', value: ['', '', '', ''], errorField: 'resetPasswordOtpError' }
        ]
      };
      
      const fields = stepFieldMap[step];
      if (fields) {
        fields.forEach(({ field, value, errorField }) => {
          if (this[field] !== undefined) {
            this[field] = value;
          }
          if (errorField && this[errorField] !== undefined) {
            this[errorField] = false;
          }
        });
      }
    },
    // ==================== 验证码相关方法 ====================
    
    /**
     * 显示验证码验证弹窗（和 login.vue 一样的方式）
     * @param {Function} callback - 验证成功后的回调函数
     */
    showCaptcha(callback) {
      this.captchaCallback = callback;
      if (this.$refs.captchaInstance) {
        this.$refs.captchaInstance.showGeetest();
      }
    },
    /** 用户关闭拼图：解除继续按钮禁用，允许再次触发 */
    onLoginCaptchaClosed() {
      if (this.sendingOtp) return;
      this.captchaCallback = null;
      this.loading = false;
    },
    /**
     * 处理验证码验证成功回调（和 login.vue 一样的方式）
     * @param {Object} captchaVerifyParam - 验证码验证参数
     * @param {Function} resolve - Promise resolve函数
     */
    finishSlideGeetest(captchaVerifyParam, resolve) {
      // 同步立刻写 captchaData 并触发发码，避免 $nextTick 晚一拍导致拼图关闭后「无反应」
      this.captchaData = {
        ...captchaVerifyParam,
        typename: captchaVerifyParam?.typename || 'CPS_Login'
      };

      let callbackResult = null;
      if (this.captchaCallback) {
        const cb = this.captchaCallback;
        this.captchaCallback = null;
        try {
          callbackResult = cb();
        } catch (err) {
          this.loading = false;
          this.sendingOtp = false;
          if (resolve) {
            resolve({ success: false, error: err });
          }
          return;
        }
      }

      // 业务接口慢时：等 Promise settle 再通知 Geetest，避免未发完码就结束、用户再点一次「继续」
      if (callbackResult && typeof callbackResult.then === 'function') {
        callbackResult
          .then((result) => {
            if (resolve) {
              resolve({ success: true, result });
            }
          })
          .catch((err) => {
            this.loading = false;
            if (resolve) {
              resolve({ success: false, error: err });
            }
          });
      } else if (resolve) {
        resolve({ success: true });
      }
    },
    /**
     * 处理验证码验证成功回调（兼容旧方法名）
     * @param {Object} captchaVerifyParam - 验证码验证参数
     * @param {Function} resolve - Promise resolve函数
     */
    handleCaptchaVerify(captchaVerifyParam, resolve) {
      this.finishSlideGeetest(captchaVerifyParam, resolve);
    },
    // ==================== 登录提交方法 ====================
    
    /**
     * ============================================
     * 提交登录请求（使用 newLogin 接口）
     * ============================================
     * 
     * 功能说明：
     * - 根据当前登录方式（username/email/mobile）和密码进行登录
     * - 使用新的登录接口 userService.newLogin()（对应后端 NewLoginParam，服务于登录页 login.vue）
     * - 验证通过后跳转到登录成功测试页面
     * 
     * 调用流程：
     * 1. 收集登录参数（用户名/邮箱/手机号、密码、验证码等）
     * 2. 直接调用 userService.newLogin() 进行真实 API 验证
     * 3. 验证成功返回用户数据，更新 Store 状态，跳转到 /auth/login-success-test
     * 
     * 注意：
     * - 使用新的登录接口 userService.newLogin()（专门为 new-login 页面设计）
     * - loginType 按输入收敛：Email/Phone 仅走自身；C* 走 RootLogin；其余 People
     * - 验证码参数会正确传递给后端
     */
    submitLogin() {
      this.loading = true;
      this.errorMessage = '';
      
      // 根据登录方式构建 username 参数
      let username = '';
      if (this.loginMethod === 'username') {
        username = this.username;
      } else if (this.loginMethod === 'email') {
        username = this.email;
      } else if (this.loginMethod === 'mobile') {
        // 手机号直接使用纯号码，不拼接国家码
        username = this.mobileNumber;
      }
      
      // 根据登录方式+输入计算 loginType（RootLogin/People/Email/Phone）
      const loginType = this.getLoginType(this.loginMethod, username);
      
      // 获取验证码参数
      // 注意：当 type == "password" 时，后端需要验证 Geetest 验证码，需要提供 lot_number, captcha_output, pass_token, gen_time, typename
      // 当 type == "email" 或 "mobile" 时，后端需要验证 verification 中的 VerifyGuid 和 VerifyCode
      
      // Geetest 验证码参数（当 type == "password" 时必需）
      const typename = this.captchaData?.typename || 'CPS_Login';
      const lot_number = this.captchaData?.lot_number || '';
      const captcha_output = this.captchaData?.captcha_output || '';
      const pass_token = this.captchaData?.pass_token || '';
      const gen_time = this.captchaData?.gen_time || '';
      
      // OTP 验证码参数（当 type == "email" 或 "mobile" 时必需，当前密码登录时为空）
      const verifyGuid = '';
      const verifyCode = '';
      
      // 判断是否使用验证码登录（email/mobile 使用验证码登录时为 true，否则为 false）
      // 注意：submitLogin 方法用于密码登录，所以 isVerifyCode 为 false
      const isVerifyCode = false;
      
      // 验证：当使用 username 登录时，必须提供 Geetest 验证码参数
      // 注意：email/mobile 登录时，如果之前已经完成验证码验证，captchaData 应该已存在
      if (this.loginMethod === 'username') {
        if (!lot_number || !captcha_output || !pass_token || !gen_time) {
          this.loading = false;
          this.errorMessage = '请完成验证码验证';
          return;
        }
      }
      
      if (username && this.password) {
      // 获取 countryId（当 loginType 为 Phone 时）
      const countryId = loginType === 'Phone' ? this.getCountryIdFromCode(this.countryCode) : null;
        
        // 更新 LogActionType 参数（根据登录方式）
        const logActionType = this.getLogActionType(this.loginMethod, false);
        this.updateLogActionTypeParam(logActionType, true);
        
        // 直接调用 userService.newLogin()（对应后端 NewLoginParam，服务于登录页 login.vue）
        // 注意：只在 email 和 mobile 类型且使用密码登录的情况下，才传递 SkipValidate: "1"
        // 因为用户首次进入 email/mobile 时已经完成滑动验证并发送过验证码，所以密码登录时不需要再次验证
        const skipValidate = (this.loginMethod === 'email' || this.loginMethod === 'mobile') ? "1" : undefined;
        userService.newLogin(
          username,
          this.password,
          loginType,
          typename,
          lot_number,
          captcha_output,
          pass_token,
          gen_time,
          verifyGuid,
          verifyCode,
          isVerifyCode,
          countryId,
          skipValidate
        ).then(res => {
          this.loading = false;
          
          // 添加 null 检查，防止 "Cannot read properties of null" 错误
          if (!res) {
            this.errorMessage = '网络异常，请稍后重试';
            return;
          }
          
          if (res.statusCode == 200 && res.isSuccessful) {
          // 显示登录成功加载动画
          this.isLoginSuccess = true;
          
          // 登录成功，更新 store 状态
          const { commit, menu } = this.$store;
          commit && commit('authentication/loginSuccess', res.data);
            
            // 延迟跳转，让用户看到加载动画
            setTimeout(() => {
              const query = this.$route.query || {};
              if (res.data && res.data.userType == 'ADMIN') {
                const adminMenu = this.$store && this.$store.menu;
                adminMenu?.mutations?.loginAsAdmin?.(adminMenu.state);
                this.$router.push({
                  path: '/Management/supplier',
                  query: query
                }).catch(() => {});
              } else {
                // KYB 锁定改道时 Vue Router 会 reject，忽略即可
                this.$router.push({
                  path: '/',
                  query: query
                }).catch(() => {});
              }
              
              // 触发浏览器保存密码提示
              this.$nextTick(() => {
                this.triggerBrowserPasswordSave();
              });
            }, 500); // 显示加载动画 500ms 后跳转
          } else if (res.statusCode == 1001) {
            this.$swal({
              text: this.$t ? this.$t('lbl_EMPBE_Geetest_Verification') : 'Verification failed',
              showCancelButton: false,
              confirmButtonText: this.$t ? this.$t('lbl_EMPBE_Confirm') : 'OK'
            });
          } else {
            // 处理登录失败：检查 statusCode == 999 或其他错误情况
            // 即使 isSuccessful 为 true，如果 statusCode 不是 200，也应该显示错误
            this.passwordError = true;
            const errorMsg = res?.msg?.join('; ') || 
                           res?.systemErrorMsg?.join('; ') || 
                           res?.Msg?.join('; ') ||
                           res?.data?.message ||
                           '账号或密码错误，请重试';
            this.errorMessage = errorMsg;
          }
        }).catch(error => {
          this.loading = false;
          this.errorMessage = error.message || '网络异常，请稍后重试';
        });
      } else {
        this.loading = false;
        this.errorMessage = 'Please enter username and password.';
      }
    },
    /**
     * 使用Token进行登录（用于第三方登录回调）
     * @param {string} token - 登录Token
     */
    submitLoginUsingToken(token) {
      // 使用真实的 Token 登录逻辑（和 login.vue 使用相同的 store action）
      const { dispatch } = this.$store;
        var query = this.$route.query;
      dispatch('authentication/loginByToken', { token, query });
    },
    // ==================== Contact Us 相关方法 ====================
    
    /**
     * 切换 Contact Us 模态框显示/隐藏
     */
    toggleContactUsModal() {
      this.showContactUsModal = !this.showContactUsModal;

      if (this.showContactUsModal) {
        // 首次打开时加载数据
        if (this.contactUsReasonOptions.length === 0) {
          this.loadContactUsReasons();
        }
        if (this.contactUsCountryOptions.length === 0) {
          this.loadContactUsCountries();
        }
      }
    },
    
    /**
     * 关闭 Contact Us 模态框
     */
    closeContactUsSuccessModal(event) {
      // 直接更新 Vue 数据，让组件通过 v-if 控制显示/隐藏
      // 不再需要直接操作 DOM，ContactUsSuccessModal 组件已经通过 v-if="show" 控制显示
      this.showContactUsSuccessModal = false;
    },
    closeContactUsModal() {
      this.showContactUsModal = false;
      document.body.classList.remove('cu-modal-open');
    },
    
    /**
     * 加载 Contact Us 查询原因下拉（使用本地配置，不再发送请求）
     * 根据 langid 显示对应语言的选项：936(简体), 950(繁体), 1252(英文)
     */
    loadContactUsReasons() {
      // 获取当前语言ID（936简体, 950繁体, 1252英文）
      const currentLangId = this.contactUsForm.langid || parseInt(localStorage.getItem('currentLangId')) || 1252;
      
      // 从配置中过滤出当前语言对应的选项
      this.contactUsReasonOptions = this.contactUsReasonConfig.filter(item => item.langid === currentLangId);
    },
    
    /**
     * 加载 Contact Us 国家下拉（使用统一的国家加载工具函数）
     */
    async loadContactUsCountries() {
      this.contactUsLoading = true;
      try {
        const { loadCountryOptions } = await import('../components/new-login/Common/country-loader.js');
        const langId = this.contactUsForm.langid || 1252;
        this.contactUsCountryOptions = await loadCountryOptions(langId);
      } catch (error) {
        this.contactUsCountryOptions = [];
      } finally {
        this.contactUsLoading = false;
      }
    },
    
    /**
     * 处理 ContactUs 表单数据更新
     */
    handleContactUsFormUpdate(newFormData) {
      this.contactUsForm = { ...this.contactUsForm, ...newFormData };
      // 如果更新了 Reason，同步更新 ReasonText
      if (newFormData.Reason !== undefined) {
        this.contactUsForm.ReasonText = findOptionText(this.contactUsReasonOptions, newFormData.Reason);
      }
      // 如果更新了 Country，同步更新 CountryText
      if (newFormData.Country !== undefined) {
        this.contactUsForm.CountryText = findOptionText(this.contactUsCountryOptions, newFormData.Country);
      }
    },
    
    /**
     * Contact Us Reason 下拉变化时，更新 ReasonText
     */
    onContactUsReasonChange() {
      this.contactUsForm.ReasonText = findOptionText(this.contactUsReasonOptions, this.contactUsForm.Reason);
    },
    
    /**
     * Contact Us Country 下拉变化时，更新 CountryText
     */
    onContactUsCountryChange() {
      const selected = this.contactUsCountryOptions.find(c => c.value === this.contactUsForm.Country);
      this.contactUsForm.CountryText = selected ? selected.text : '';
      this.contactUsForm.Country = selected ? selected.value : '';
    },
    
    /**
     * 提交 Contact Us 表单（直接调用原有的 ReplyCoupon/ToDoSubmit）
     */
    async submitContactUs() {
      // 表单验证
      const form = document.getElementById('cu-form-contact-us');
      if (!form.checkValidity()) {
        form.reportValidity();
        return;
      }
      
      // 验证邮箱格式
      if (!validateEmail(this.contactUsForm.Email)) {
        alert('Invalid format of email address');
        return;
      }
      
      // 去除邮箱空格
      this.contactUsForm.Email = this.contactUsForm.Email.replaceAll(' ', '');
      
      // 1. 先显示 loading
      this.contactUsSubmitting = true;
      this.contactUsLoading = true;
      
      // 2. 等待一下确保 loading 显示，然后显示验证码
      await this.$nextTick();
      setTimeout(() => {
        // 3. 显示验证码（验证码会显示在 loading 之上，因为 z-index 更高）
        this.showContactUsCaptcha(() => {
          // 验证码通过后的回调
        });
      }, 100);
    },

    /**
     * 显示 ContactUs 验证码（和 login.vue 一样的方式）
     */
    showContactUsCaptcha(callback) {
      // callback 现在通过 finishContactUsSlideGeetest 直接处理，不需要存储
      if (this.$refs.contactUsCaptchaInstance) {
        this.$refs.contactUsCaptchaInstance.showGeetest();
      }
    },

    /**
     * 处理 ContactUs 验证码验证成功回调（和 login.vue 一样的方式）
     * @param {Object} captchaVerifyParam - Geetest 验证码返回的数据，包含 lot_number, captcha_output, pass_token, gen_time
     * @param {Function} resolve - 可选的 resolve 回调
     */
    async finishContactUsSlideGeetest(captchaVerifyParam, resolve) {
      this.$nextTick(async () => {
        // 设置本地状态
        this.contactUsCaptchaData = captchaVerifyParam;
        this.contactUsForm.captchaVerifyParam = JSON.stringify(captchaVerifyParam);
        
        // 更新隐藏字段（为了向后兼容，保留 DOM 操作）
        if (captchaVerifyParam) {
          const lotNumberInput = document.getElementById('cu-lot-number');
          const captchaOutputInput = document.getElementById('cu-captcha-output');
          const passTokenInput = document.getElementById('cu-pass-token');
          const genTimeInput = document.getElementById('cu-gen-time');
          const typeNameInput = document.getElementById('cu-type-name');
          
          if (lotNumberInput) lotNumberInput.value = captchaVerifyParam.lot_number || '';
          if (captchaOutputInput) captchaOutputInput.value = captchaVerifyParam.captcha_output || '';
          if (passTokenInput) passTokenInput.value = captchaVerifyParam.pass_token || '';
          if (genTimeInput) genTimeInput.value = captchaVerifyParam.gen_time || '';
          if (typeNameInput) typeNameInput.value = 'CPS_Login'; // 设置 typename
        }
        
        // 调用 resolve
        if (resolve) {
          resolve(true);
        }
        
        // 执行实际提交
        await this.doSubmitContactUs();
      });
    },
    
    /**
     * 执行实际提交
     * 严格参考前端指导文件：根据Reason值从数据库动态获取邮件配置
     */
    async doSubmitContactUs() {
      try {
        const { 
          sendFormEmail, 
          getEmailProjectByReason, 
          getEmailConfigFromDatabase 
        } = await import('../components/new-login/Common/email-sender.js');
        
        // 获取 captcha_ticket 和 captcha_randstr（用于腾讯验证码，Geetest 可能不需要，但为了完整性添加）
        const captchaTicketInput = document.getElementById('cu-captcha-ticket');
        const captchaRandstrInput = document.getElementById('cu-captcha-randstr');
        
        // 根据Reason值映射到EmailProject（严格参考前端指导文件）
        const reason = this.contactUsForm.Reason;
        const emailProject = getEmailProjectByReason(reason);
        
        // 从数据库获取邮件配置
        const showId = this.contactUsForm.showid || DEFAULT_SHOW_ID;
        const langId = this.contactUsForm.langid || getCurrentLangId();
        const showCode = this.contactUsForm.showcode || DEFAULT_SHOW_CODE;
        const dbEmailConfig = await getEmailConfigFromDatabase(showId, langId, emailProject, showCode);
        
        // 构建表单数据
        const formDataToSend = {
          fid: this.contactUsForm.fid,
          showid: showId,
          showcode: this.contactUsForm.showcode,
          langid: langId,
          hidCodepage: this.contactUsForm.hidCodepage || String(langId) || '1252',
          submitPageURL: this.contactUsForm.submitPageURL,
          IsNewValidCode: this.contactUsForm.IsNewValidCode,
          isNotNeedValidate: this.contactUsForm.isNotNeedValidate,
          captchaVerifyParam: this.contactUsForm.captchaVerifyParam,
          // Contact Us 表单字段
          Reason: reason,
          ReasonText: this.contactUsForm.ReasonText,
          message: this.contactUsForm.message,
          name: this.contactUsForm.name,
          Company: this.contactUsForm.Company,
          Country: this.contactUsForm.Country,
          CountryText: this.contactUsForm.CountryText,
          Mobile: this.contactUsForm.Mobile,
          Email: this.contactUsForm.Email,
          hdPrivacy: this.contactUsForm.hdPrivacy,
          hdShare: this.contactUsForm.hdShare,
          // 验证码相关字段（可选）
          captcha_ticket: captchaTicketInput ? captchaTicketInput.value : '',
          captcha_randstr: captchaRandstrInput ? captchaRandstrInput.value : ''
        };
        
        // 邮件配置（从数据库获取，严格参考前端指导文件）
        // TO（收件人）= 用户填写的邮箱（发送确认邮件）
        // BCC/CC（密送/抄送）= 数据库配置的内部人员邮箱（发送通知邮件）
        const emailConfig = {
          email_to: this.contactUsForm.Email || '', // 用户邮箱 - 发送确认邮件
          email_from: dbEmailConfig.FromEmail || 'emarketplace@adsale.com.hk',
          email_fromName: dbEmailConfig.FromName || '',
          email_replyTo: dbEmailConfig.ReplyTo || '',
          email_bcc: dbEmailConfig.BCC || '',
          email_cc: dbEmailConfig.CC || '',
          email_subject: dbEmailConfig.Title || 'eMarketplace - Contact Us',
          email_confirm_message: 'Send form Success'
        };
        
        // 发送邮件
        const result = await sendFormEmail({
          formData: formDataToSend,
          captchaData: this.contactUsCaptchaData,
          emailConfig: emailConfig,
          formType: 'contact-us'
        });
        
        // 处理返回结果
        handleFormSubmitResult(result, SUBMIT_STATUS_CODE, {
          onCaptchaFailed: () => {
            this.contactUsLoading = false;
            this.contactUsSubmitting = false;
          },
          onValidationError: () => {
            this.contactUsLoading = false;
            this.contactUsSubmitting = false;
          },
          onSuccess: () => {
            // 提交成功，显示成功弹框
            this.contactUsLoading = false;
            this.contactUsSubmitting = false;
            // 先关闭 ContactUs 表单
            this.showContactUsModal = false;
            // 然后显示成功弹框
            this.showContactUsSuccessModal = true;
            this.resetContactUsForm();
          },
          onError: () => {
            this.contactUsLoading = false;
            this.contactUsSubmitting = false;
          }
        });
      } catch (error) {
        alert('提交失败，请稍后重试');
        this.contactUsSubmitting = false;
        this.contactUsLoading = false;
      }
    },
    
    /**
     * 重置 Contact Us 表单
     * 注意：邮件配置现在从数据库动态获取，不再使用硬编码的常量
     */
    resetContactUsForm() {
      // 保存当前配置值（showid, showcode, submitPageURL 等）
      const contactUsConfig = getContactUsConfig('supplier');
      const currentShowid = this.contactUsForm.showid || contactUsConfig.showid || DEFAULT_SHOW_ID;
      const currentShowcode = this.contactUsForm.showcode || contactUsConfig.showcode || DEFAULT_SHOW_CODE;
      const currentSubmitPageURL = this.contactUsForm.submitPageURL || contactUsConfig.submitPageURL;
      const currentLangid = this.contactUsForm.langid || getCurrentLangId();
      const currentHidCodepage = this.contactUsForm.hidCodepage || String(currentLangid);
      
      this.contactUsForm = {
        fid: contactUsConfig.fid,
        hidCodepage: currentHidCodepage,
        showid: currentShowid,
        showcode: currentShowcode,
        langid: currentLangid,
        Reason: null,
        ReasonText: '',
        message: '',
        name: '',
        Company: '',
        Country: '',
        CountryText: '',
        Mobile: '',
        Email: '',
        hdPrivacy: false,
        hdShare: true,
        captchaVerifyParam: '',
        submitPageURL: currentSubmitPageURL,
        IsNewValidCode: 'true',
        isNotNeedValidate: 'false' // 保持为 false 以强制验证码验证
        // 注意：邮件配置现在从数据库动态获取，在提交时根据Reason值获取
      };
    }
  },
  watch: {
    // 监听当前步骤，当切换到密码输入步骤时自动聚焦密码输入框
    currentStep(newStep) {
      if (newStep === 'password') {
        this.$nextTick(() => {
          if (this.$refs.passwordInput) {
            this.$refs.passwordInput.focus();
          }
        });
      }
      if (newStep === 'username') {
        this.$nextTick(() => {
          this.tryFocusUsernameInput();
          this.startAutofillPolling();
        });
      } else {
        this.stopAutofillPolling();
      }
    }
  }
};
</script>

<style scoped>
/* ==================== 账号密码登录页面样式（仅影响 login-step-username） ==================== */

/* 账号密码登录步骤整体间距 */
.login-step.login-step-username {
  gap: 24px !important;
}

/* 账号密码表单样式 - 精确控制间距 */
.login-step.login-step-username .username-password-form {
  display: flex;
  flex-direction: column;
  gap: 16px !important; /* Account ID 和 Password 输入框之间 16px */
  width: 100%;
}

/* 账号输入区域 */
.login-step.login-step-username .form-group.username-group {
  gap: 16px !important;
  margin: 0 !important;
}

/* 密码输入区域 */
.login-step.login-step-username .form-group.password-group {
  margin: 0 !important;
}

/* 忘记链接容器 */
.login-step.login-step-username .forgot-links-wrapper {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin: 0 !important;
}

.login-step.login-step-username .forgot-links-wrapper .forgot-text-container {
  width: 100%;
  display: inline-flex;
  justify-content: flex-start;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
  align-content: center;
}

.login-step.login-step-username .forgot-links-wrapper .forgot-text-static {
  color: rgba(0, 0, 0, 0.40);
  font-size: 14px;
  font-family: Inter, sans-serif;
  font-weight: 500;
  line-height: 14px;
  word-wrap: break-word;
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.login-step.login-step-username .forgot-links-wrapper .forgot-link-clickable {
  color: rgba(0, 0, 0, 0.40);
  font-size: 14px;
  font-family: Inter, sans-serif;
  font-weight: 500;
  line-height: 14px;
  word-wrap: break-word;
  text-align: center;
  display: inline-flex;
  flex-direction: column;
  justify-content: center;
  text-decoration: none;
  cursor: pointer;
  transition: color 0.2s ease;
  letter-spacing: unset;
}

.login-step.login-step-username .forgot-links-wrapper .forgot-link-clickable:hover {
  color: rgba(0, 0, 0, 0.60);
}

/* Login 按钮间距 - Forgot 链接和 Login 按钮之间 24px */
.login-step.login-step-username .username-password-form > .username-continue-btn {
  margin-top: 8px !important; /* form gap 16px + margin-top 8px = 24px */
}

/* Email登录页面间距优化 */
.login-step.login-step-email {
  gap: calc(16px * 0.9) !important; /* 减少10%，使页面更紧凑 */
}

/* Email和Mobile登录页面：Continue按钮与上方输入框的间距减少30%（20%+10%） */
.login-step.login-step-email .form-group + .btn-primary,
.login-step.login-step-email form .form-group + button[type="submit"] {
  margin-top: calc(-16px * 0.3) !important; /* 动态计算：16px * 30% = 4.8px */
}

/* Mobile登录页面间距优化 */
.login-step.login-step-mobile {
  gap: calc(24px * 0.7) !important; /* 减少30%（20%+10%），动态计算 */
}

.login-step.login-step-mobile .form-group + .btn-primary,
.login-step.login-step-mobile form .form-group + button[type="submit"] {
  margin-top: calc(-24px * 0.3) !important; /* 动态计算：24px * 30% = 7.2px */
}

/* 确保 form 标签不影响布局 */
.login-step.login-step-email form,
.login-step.login-step-mobile form {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
}

.input-wrapper {
  overflow: visible;
}

/* 标签容器（在输入框边框上方） */
.input-label-container {
  height: auto;
  min-height: 2px;
  padding-left: 4px;
  padding-right: 4px;
  padding-top: 0;
  padding-bottom: 0;
  background: white;
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0;
  position: absolute;
  top: -1px;
  left: 12px;
  z-index: 10;
}

/* 确保标签文字在容器内正确显示 */
.input-label-container .input-label {
  line-height: 12px;
  height: auto;
  margin: 0;
  padding: 0;
  white-space: nowrap;
  display: inline;
  color: rgba(0, 0, 0, 0.60);
  font-size: 12px;
  font-family: 'Roboto', sans-serif;
  font-weight: 400;
  letter-spacing: 0.15px;
}

/* 通用 placeholder 样式（用于所有输入框） */
.input-container-placeholder {
  width: 100%;
  position: relative;
  overflow: visible;
  z-index: 0;
}

.input-container-with-icon {
  overflow: visible !important;
}

/* Username 输入框特殊处理（有图标） */
.input-container-with-icon .form-input-placeholder {
  padding-right: 44px;
}

/* 其他错误信息样式（特殊宽度设置） */
.email-error-message,
.mobile-error-message,
.password-error-message {
  width: 100%;
  letter-spacing: 0.40px;
  word-wrap: break-word;
}

/* Password 输入框特殊处理 */
.password-input-wrapper .input-container-placeholder {
  width: 100%;
}

.password-input-wrapper .form-input-placeholder {
  padding-right: 48px;
}

.password-input-wrapper .form-input-placeholder.input-error ~ .password-toggle svg {
  stroke: #D32F2F;
}

/* 处理浏览器自动填充 - 隐藏占位符 */
.input-container-placeholder:has(.form-input-placeholder:-webkit-autofill) .input-placeholder {
  opacity: 0;
  visibility: hidden;
}

.input-container-placeholder:has(.form-input-placeholder:-webkit-autofill) .input-label {
  display: inline-flex !important;
}

.error-message {
  color: #CD1515;
  font-size: 14px;
  font-family: 'Inter', sans-serif;
  padding: 12px;
  background: rgba(0, 0, 0, 0.05);
  border-radius: 4px;
  text-align: center;
}

.input-error {
  border-color: #CD1515 !important;
}

.step-header {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.back-btn {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
}

.back-btn:hover {
  background: rgba(0, 0, 0, 0.05);
  border-radius: 50%;
}

.back-btn .back-icon {
  width: 20px;
  height: 20px;
  transform: scaleX(-1);
}

/* Responsive Design */
@media (max-width: 768px) {
  .new-login-card {
    padding: 32px 16px;
  }

  .step-title {
    font-size: 24px;
    line-height: 32px;
  }

}

/* ==================== 登录成功加载动画 ==================== */
.login-success-loading {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.95);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 24px;
  z-index: 10000;
  backdrop-filter: blur(4px);
}

.loading-spinner {
  width: 48px;
  height: 48px;
  border: 4px solid rgba(115, 102, 255, 0.2);
  border-top-color: #7366FF;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-text {
  color: rgba(0, 0, 0, 0.80);
  font-size: 16px;
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  line-height: 24px;
}

.login-success-blur {
  filter: blur(2px);
  pointer-events: none;
  opacity: 0.7;
}
/* 按钮样式 */
.btn-secondary {
  width: 100%;
  height: 42px;
  box-sizing: border-box;
}
</style>

<!-- 引入共享样式（非 scoped，确保全局应用） -->
<style>
@import '../components/new-login/Common/new-login-shared.css';
@import '../components/new-login/Common/new-login-layout.css';
@import '../components/new-login/Common/new-login-inputs.css';
@import '../components/new-login/Common/new-login-otp.css';
@import '../components/new-login/Common/new-login-email-input.css';
@import '../components/new-login/Common/new-login-select-input.css';
@import '../components/new-login/Common/new-login-reset-password.css';
@import '../components/new-login/Common/new-login-modals.css';
@import '../components/new-login/Common/new-login-contact-us.css';

/* OTP 信息样式 - email/mobile 换行并加粗 */
.otp-info-line {
  color: rgba(0, 0, 0, 0.80);
  font-size: 14px;
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  line-height: 18.20px;
  word-wrap: break-word;
}

.otp-info-target {
  color: rgba(0, 0, 0, 0.80);
  font-size: 14px;
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  line-height: 18.20px;
  word-wrap: break-word;
}



.btn-secondary-content {
  flex: 1 1 0;
  text-box-trim: trim-both;
  text-box-edge: cap alphabetic;
  text-align: center;
  justify-content: center;
  align-items: center;
  display: flex;
  flex-direction: column;
  width: 100%;
  line-height: 18.20px;
}

.btn-secondary-text {
  color: rgba(0, 0, 0, 0.80);
  font-size: 14px;
  font-family: Inter, sans-serif;
  font-weight: 600;
  line-height: 18.20px;
  word-wrap: break-word;
  display: inline;
}

/* 按钮中的邮箱/手机号加粗 */
.btn-secondary-target {
  color: rgba(0, 0, 0, 0.80);
  font-size: 14px;
  font-family: Inter, sans-serif;
  font-weight: 700;
  line-height: 18.20px;
  word-wrap: break-word;
  display: inline;
}
</style>

